import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUpRight, ChevronDown, Eye, EyeOff, LifeBuoy, MessageCircle, Radar, SearchX, Send, Trash2, X } from 'lucide-react';
import type { BotApi } from '../adapters/BotApi';
import type { ActionKey, BotCommand, BotSlot, BotStatus, IntelligenceRequest, LinkingChatMessage, LinkingSession, ReconnectSettings } from '../domain/types';
import { actionAvailability } from '../domain/permissions';
import { onboardingState } from '../domain/onboarding';
import { EmptyState, ErrorState, Panel, formatError } from './components';
import { formatDuration, formatTimestamp, secondsSince } from './format';
import { ActionSettingsPanel } from './ActionSettingsPanel';
import { OperationModal, type OperationModalTone } from './OperationModal';
import { actionPayloadFromSettings, defaultDashboardSettings, mergeDashboardSettings } from './actionSettings';
import { useI18n, type TranslationKey } from '../i18n';
import { DOWNLOADABLE_APP_URL } from '../config/appLinks';

const actions: { key: ActionKey; labelKey: TranslationKey; tone: 'primary' | 'danger' }[] = [
  { key: 'auto_bot', labelKey: 'dashboard.autoBot', tone: 'primary' },
  { key: 'collect', labelKey: 'dashboard.collect', tone: 'primary' },
  { key: 'train_troops', labelKey: 'dashboard.trainTroops', tone: 'primary' },
  { key: 'auto_hunt', labelKey: 'dashboard.autoHunt', tone: 'primary' },
  { key: 'stop', labelKey: 'dashboard.stopBot', tone: 'danger' }
];

const DEFAULT_VISIBLE_INTELLIGENCE_RESULTS = 5;
const MIN_INTELLIGENCE_QUERY_LENGTH = 3;
const LINKING_CAPTURE_WAIT_MAX_MS = 9000;
const LINKING_PREPARATION_TIMEOUT_MS = 90000;
const LINKING_INPUT_WAIT_TIMEOUT_MS = 30000;
const SLOT_CLAIM_RETRY_MS = 60000;
const USER_ACTION_MODAL_TIMEOUT_MS = 300000;
const USER_ACTION_SUCCESS_AUTO_CLOSE_MS = 3000;
type IntelligenceResultSort = 'power' | 'name';
type SlotClaimState = 'idle' | 'no_capacity' | 'error';
type UserActionModalPhase = 'sending' | 'queued' | 'delivered' | 'running' | 'success' | 'error' | 'timeout';

interface UserActionModalState {
  action: ActionKey;
  startedAt: number;
  phase: UserActionModalPhase;
  sawCommand: boolean;
  sawWorker: boolean;
  commandId?: string | null;
  error?: string | null;
}

export interface ReconnectMenuControls {
  settings: ReconnectSettings;
  running: boolean;
  canSave: boolean;
  canReconnect: boolean;
  onSettingsChange: (settings: ReconnectSettings) => void;
  onReconnectNow: () => void;
}

export function DashboardPage({
  api,
  preferredSlotId,
  onSlotStateChange,
  onReconnectMenuChange
}: {
  api: BotApi;
  preferredSlotId?: string | null;
  onSlotStateChange?: (state: { license: string; linked: boolean; licenseDetail: string; isStripe: boolean }) => void;
  onReconnectMenuChange?: (controls: ReconnectMenuControls | null) => void;
}) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [message, setMessage] = useState('');
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [settings, setSettings] = useState(defaultDashboardSettings);
  const [linkText, setLinkText] = useState('');
  const [linkTextVisible, setLinkTextVisible] = useState(false);
  const [optimisticAction, setOptimisticAction] = useState<{ action: ActionKey; startedAt: number } | null>(null);
  const [tutorialDismissed, setTutorialDismissed] = useState(true);
  const [tutorialDoNotShow, setTutorialDoNotShow] = useState(false);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const [linkingModal, setLinkingModal] = useState<{ title: string; body: string } | null>(null);
  const [reconnectModal, setReconnectModal] = useState<ReconnectModalState | null>(null);
  const [userActionModal, setUserActionModal] = useState<UserActionModalState | null>(null);
  const [linkingOperationStartedAt, setLinkingOperationStartedAt] = useState<number | null>(null);
  const [lastLinkingInputAt, setLastLinkingInputAt] = useState<number | null>(null);
  const [lastLinkingInputScreenshotUrl, setLastLinkingInputScreenshotUrl] = useState<string | null>(null);
  const [linkingReadySessionId, setLinkingReadySessionId] = useState<string | null>(null);
  const [autoCompletingLinkingSessionId, setAutoCompletingLinkingSessionId] = useState<string | null>(null);
  const [dismissedInputWaitAt, setDismissedInputWaitAt] = useState<number | null>(null);
  const [linkingEmailSent, setLinkingEmailSent] = useState(false);
  const [slotClaimState, setSlotClaimState] = useState<SlotClaimState>('idle');
  const [lastSlotClaimAttemptAt, setLastSlotClaimAttemptAt] = useState<number | null>(null);
  const [reconnectSettings, setReconnectSettings] = useState<ReconnectSettings>({
    autoEnabled: false,
    delaySeconds: 300
  });
  const skipSettingsSaveForKey = useRef<string | null>(null);
  const remoteSettingsSaveTimer = useRef<number | null>(null);
  const reconnectMenuSignature = useRef<string>('');

  const userQuery = useQuery({ queryKey: ['user'], queryFn: () => api.getCurrentUser() });
  const slotsQuery = useQuery({
    queryKey: ['slots', userQuery.data?.role],
    queryFn: () => api.listSlots({ scope: userQuery.data?.role === 'admin' ? 'all' : 'own' }),
    enabled: Boolean(userQuery.data),
    refetchInterval: 5000
  });
  const slots = slotsQuery.data || [];
  const claimSlotMutation = useMutation({
    mutationFn: () => api.claimNextAvailableSlot!(),
    onMutate: () => {
      setLastSlotClaimAttemptAt(Date.now());
      setSlotClaimState('idle');
      setMessage('');
    },
    onSuccess: async (claimedSlot) => {
      if (!claimedSlot) {
        setSlotClaimState('no_capacity');
        return;
      }
      setSlotClaimState('idle');
      setMessage(t('linking.slotClaimed'));
      await queryClient.invalidateQueries({ queryKey: ['slots'] });
    },
    onError: (error) => {
      setSlotClaimState('error');
      setMessage(formatError(error));
    }
  });
  const selectedSlot = useMemo(() => {
    if (preferredSlotId) {
      const preferred = slots.find((slot) => slot.id === preferredSlotId);
      if (preferred) return preferred;
    }
    if (userQuery.data?.email) {
      const ownSlot = slots.find((slot) => slot.userEmail === userQuery.data?.email);
      if (ownSlot) return ownSlot;
    }
    return slots[0] || null;
  }, [preferredSlotId, slots, userQuery.data?.email]);

  const statusQuery = useQuery({
    queryKey: ['status', selectedSlot?.id],
    queryFn: () => api.getSlotStatus(selectedSlot!.id),
    enabled: Boolean(selectedSlot),
    refetchInterval:
      reconnectModal?.kind === 'working'
        ? 1500
        : selectedSlot && !selectedSlot.linked
          ? 500
          : 5000
  });

  const commandsQuery = useQuery({
    queryKey: ['commands', selectedSlot?.id],
    queryFn: () => api.listCommands(selectedSlot!.id),
    enabled: Boolean(selectedSlot),
    refetchInterval: 5000
  });

  const linkingSessionQuery = useQuery({
    queryKey: ['linking-session', selectedSlot?.id],
    queryFn: () => api.getActiveLinkingSession!(selectedSlot!.id),
    enabled: Boolean(selectedSlot && !selectedSlot.linked && api.getActiveLinkingSession),
    refetchInterval: 500
  });

  const linkingChatSessionId = linkingSessionQuery.data?.id || null;
  const linkingChatQuery = useQuery({
    queryKey: ['linking-chat', linkingChatSessionId],
    queryFn: () => api.getLinkingChatMessages!(linkingChatSessionId!),
    enabled: Boolean(linkingChatSessionId && api.getLinkingChatMessages),
    refetchInterval: 3000
  });

  const slotSettingsQuery = useQuery({
    queryKey: ['slot-settings', selectedSlot?.id],
    queryFn: () => api.getSlotSettings!(selectedSlot!.id),
    enabled: Boolean(selectedSlot && api.getSlotSettings),
    staleTime: 30000
  });

  const intelligenceAllowed = isIntelligenceAllowed(selectedSlot);
  const autoShieldAllowed = isAutoShieldAllowed(selectedSlot);
  const intelligenceQuery = useQuery({
    queryKey: ['intelligence-requests', userQuery.data?.id],
    queryFn: () => api.listIntelligenceRequests!(),
    enabled: Boolean(intelligenceAllowed && api.listIntelligenceRequests),
    refetchInterval: 5000
  });
  const intelligenceRequests = intelligenceQuery.data || [];
  const visibleIntelligenceRequests = intelligenceRequests.filter((request) => !request.hiddenAt);
  const intelligenceQuota = intelligenceQuotaState(selectedSlot, intelligenceRequests);

  const commandMutation = useMutation({
    mutationFn: (action: ActionKey) =>
      api.sendCommand({
        slotId: selectedSlot!.id,
        action,
        payload: actionPayloadFromSettings(action, settings)
      }),
    onSuccess: async (command) => {
      setUserActionModal((current) =>
        current
          ? {
              ...current,
              commandId: command.commandId,
              phase: userActionPhaseFromCommandStatus(command.status),
              sawCommand: true,
              error: isUserActionFailureCommandStatus(command.status) ? command.error || command.status : current.error
            }
          : current
      );
      setMessage('');
      await queryClient.invalidateQueries({ queryKey: ['commands', selectedSlot?.id] });
      await queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] });
    },
    onError: (error) => {
      setOptimisticAction(null);
      setUserActionModal((current) =>
        current
          ? {
              ...current,
              phase: 'error',
              error: formatError(error)
            }
          : current
      );
      setMessage(formatError(error));
    }
  });

  const reconnectSettingsMutation = useMutation({
    mutationFn: (next: ReconnectSettings) => api.saveReconnectSettings!(selectedSlot!.id, next),
    onSuccess: async () => {
      setMessage(t('dashboard.reconnectSaved'));
      await queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] });
    },
    onError: (error) => setMessage(formatError(error))
  });

  const startLinkingMutation = useMutation({
    mutationFn: async () => {
      let slotId = selectedSlot!.id;
      if (api.mode === 'supabase' && api.claimNextAvailableSlot && !selectedSlot!.linked) {
        const claimedSlot = await api.claimNextAvailableSlot();
        if (claimedSlot?.id) slotId = claimedSlot.id;
      }
      return api.startLinkingSession!(slotId);
    },
    onSuccess: async () => {
      setMessage(t('linking.started'));
      setLinkingReadySessionId(null);
      setAutoCompletingLinkingSessionId(null);
      setLastLinkingInputAt(null);
      setLastLinkingInputScreenshotUrl(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['slots'] }),
        queryClient.invalidateQueries({ queryKey: ['linking-session'] }),
        queryClient.invalidateQueries({ queryKey: ['status'] })
      ]);
    },
    onError: (error) => setMessage(formatError(error))
  });

  const sendLinkInputMutation = useMutation({
    mutationFn: (input: { session: LinkingSession; eventType: 'tap' | 'text' | 'key'; payload: Record<string, unknown> }) =>
      api.sendLinkInput!(input.session.id, input.eventType, input.payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] });
    },
    onError: async (error) => {
      const clean = formatError(error);
      setMessage(clean);
      if (isLinkingExpiredError(error)) {
        setLinkingModal({ title: t('linking.expiredTitle'), body: t('linking.expiredBody') });
        await queryClient.invalidateQueries({ queryKey: ['linking-session', selectedSlot?.id] });
      } else if (isLinkingRateLimitError(error)) {
        setLinkingModal({ title: t('linking.slowDownTitle'), body: t('linking.slowDownBody') });
      }
    }
  });

  const completeLinkingMutation = useMutation({
    mutationFn: (session: LinkingSession) => api.completeLinkingSession!(session.id),
    onSuccess: async () => {
      setMessage(t('linking.completed'));
      setLinkingReadySessionId(null);
      setAutoCompletingLinkingSessionId(null);
      setLastLinkingInputAt(null);
      setLastLinkingInputScreenshotUrl(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['slots'] }),
        queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] }),
        queryClient.invalidateQueries({ queryKey: ['linking-session', selectedSlot?.id] })
      ]);
    },
    onError: (error) => setMessage(formatError(error))
  });

  const cancelLinkingMutation = useMutation({
    mutationFn: (input: { session: LinkingSession; reason?: string }) =>
      api.cancelLinkingSession!(input.session.id, input.reason),
    onSuccess: async (_result, input) => {
      if (input.reason === 'wrong_account') {
        setMessage(t('linking.wrongAccountCancelled'));
        setLinkingModal({ title: t('linking.wrongAccountTitle'), body: t('linking.wrongAccountBody') });
      } else {
        setMessage(t('linking.cancelled'));
      }
      setLinkingReadySessionId(null);
      setAutoCompletingLinkingSessionId(null);
      setLastLinkingInputAt(null);
      setLastLinkingInputScreenshotUrl(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['linking-session', selectedSlot?.id] }),
        queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] })
      ]);
    },
    onError: (error) => setMessage(formatError(error))
  });

  const requestAssistanceMutation = useMutation({
    mutationFn: (sessionId: string) => api.requestLinkingAssistance!(sessionId),
    onSuccess: async () => {
      setMessage(t('linking.assistanceRequested'));
      await queryClient.invalidateQueries({ queryKey: ['linking-session', selectedSlot?.id] });
    },
    onError: (error) => setMessage(formatError(error))
  });

  const sendChatMutation = useMutation({
    mutationFn: (input: { sessionId: string; body: string }) =>
      api.sendLinkingChatMessage!(input.sessionId, input.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['linking-chat', linkingChatSessionId] });
    },
    onError: (error) => setMessage(formatError(error))
  });

  const reconnectNowMutation = useMutation({
    mutationFn: () => api.reconnectNow!(selectedSlot!.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['status', selectedSlot?.id] }),
        queryClient.invalidateQueries({ queryKey: ['commands', selectedSlot?.id] })
      ]);
    },
    onError: (error) => {
      setReconnectModal((current) =>
        current ? { ...current, kind: 'error', reason: reconnectErrorReason(error) } : current
      );
    }
  });

  function startReconnect() {
    if (!api.reconnectNow || !selectedSlot) return;
    setReconnectModal({ startedAt: Date.now(), sawWorking: false, kind: 'working', reason: null });
    reconnectNowMutation.mutate();
  }

  const intelligenceMutation = useMutation({
    mutationFn: (request: { playerName: string; regionId?: string }) =>
      api.submitIntelligenceRequest!(request.playerName, request.regionId),
    onSuccess: async () => {
      setMessage(t('intelligence.requestQueued'));
      setQuotaModalOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['intelligence-requests', userQuery.data?.id] });
    },
    onError: (error) => {
      if (isIntelligenceQuotaError(error)) {
        setQuotaModalOpen(true);
        setMessage('');
        return;
      }
      setMessage(formatError(error));
    }
  });

  const deleteIntelligenceMutation = useMutation({
    mutationFn: (requestId: string) => api.deleteIntelligenceRequest!(requestId),
    onSuccess: async () => {
      setMessage(t('intelligence.deleteSuccess'));
      await queryClient.invalidateQueries({ queryKey: ['intelligence-requests', userQuery.data?.id] });
    },
    onError: (error) => setMessage(formatError(error))
  });

  const commands = commandsQuery.error ? [] : commandsQuery.data || [];
  const status = statusQuery.data || null;

  // Drive the reconnect progress modal from the live worker status so the user
  // sees clear "reconnecting -> done/failed" feedback instead of guessing.
  useEffect(() => {
    setReconnectModal((current) => advanceReconnectModal(current, status));
  }, [status]);

  // While working, flag "taking longer than usual" after a soft threshold (keeps
  // tracking, lets the user close) and only give up after a long absolute window.
  // Auto-dismiss shortly after a successful reconnect.
  useEffect(() => {
    if (!reconnectModal) return;
    if (reconnectModal.kind === 'working') {
      const id = window.setInterval(() => {
        setReconnectModal((current) => {
          if (!current || current.kind !== 'working') return current;
          const elapsed = Date.now() - current.startedAt;
          if (elapsed > RECONNECT_MODAL_TIMEOUT_MS) {
            return { ...current, kind: 'error', reason: 'timeout' };
          }
          if (elapsed > RECONNECT_MODAL_SLOW_MS && !current.slow) {
            return { ...current, slow: true };
          }
          return current;
        });
      }, 1000);
      return () => window.clearInterval(id);
    }
    if (reconnectModal.kind === 'success') {
      const id = window.setTimeout(() => setReconnectModal(null), 3000);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [reconnectModal?.kind, reconnectModal?.startedAt]);
  const settingsKey =
    userQuery.data && selectedSlot ? `white-bot:dashboard-settings:${userQuery.data.id}:${selectedSlot.slotKey}` : null;
  const tutorialKey =
    userQuery.data && selectedSlot ? `white-bot:onboarding-tutorial:${userQuery.data.id}:${selectedSlot.slotKey}` : null;
  const slotCommandAllowed = Boolean(selectedSlot);
  const bridgeAgeSeconds = secondsSince(status?.agentLastSeenAt);
  const bridgeFresh = bridgeAgeSeconds != null && bridgeAgeSeconds <= 30;
  const hostedBridgeReady =
    api.mode !== 'supabase' || (bridgeFresh && status?.bridgeStatus !== 'offline' && status?.localAvailable !== false);
  const provisioningBlocked = api.mode === 'supabase' && status?.provisioning?.status === 'blocked';
  const onboarding = onboardingState(selectedSlot, status, api.mode);
  const isAdminUser = userQuery.data?.role === 'admin';
  const hideSecretBotLogs = !isAdminUser;
  const unavailableTitle = isAdminUser ? t('activity.bridgeOffline') : t('activity.servicePreparing');
  const unavailableMessage = isAdminUser ? t('dashboard.bridgeUnavailable') : t('dashboard.servicePreparing');
  const latestLogLine = cleanLogLine(status?.latestLogLine, hideSecretBotLogs);
  const liveStatusLine = latestLogLine || onboarding.detail || onboarding.title || t('dashboard.waitingStatus');
  const liveTiming = liveActionTiming(status, clockNow, statusQuery.dataUpdatedAt);
  const linkingSession = linkingSessionQuery.data || null;
  const linkingSessionId = linkingSession?.id || null;
  const linkingChatMessages = linkingChatQuery.data || [];
  const linkingAssistanceRequested = Boolean(linkingSession?.assistanceRequestedAt);
  const linkingOperatorActive = Boolean(linkingSession?.operatorActive);
  const linkingPhase = linkingAssistantPhase(status?.linkingAssistant);
  const linkingAutomationActive = isLinkingAssistantAutomationActive(status?.linkingAssistant);
  const linkingRecoveryBlocked = isLinkingAssistantRecoveryBlocked(status?.linkingAssistant);
  const linkingFlowComplete = isLinkingAssistantComplete(status?.linkingAssistant);
  const linkingInputDeliveredWithoutState = isLinkingInputLog(latestLogLine) && !status?.linkingAssistant;
  const linkingInputKind =
    (linkingPhase && isGooglePasswordPhase(linkingPhase)) || linkingEmailSent ? 'password' : 'email';
  const detectedLinkingAssistantReady = isLinkingAssistantReady(status?.linkingAssistant, latestLogLine);
  const linkingInputReady = Boolean(
    linkingSessionId &&
      !linkingAutomationActive &&
      !linkingRecoveryBlocked &&
      !linkingFlowComplete &&
      (detectedLinkingAssistantReady || linkingReadySessionId === linkingSessionId)
  );
  const linkingPreparing = Boolean(
    linkingSession &&
      !linkingInputReady &&
      !linkingAutomationActive &&
      !linkingRecoveryBlocked &&
      !linkingFlowComplete &&
      !linkingInputDeliveredWithoutState &&
      !selectedSlot?.linked
  );
  const linkingCaptureBusy = Boolean(
    linkingSession &&
    (linkingInputReady || linkingInputDeliveredWithoutState) &&
    (sendLinkInputMutation.isPending ||
      (lastLinkingInputAt && clockNow - lastLinkingInputAt < LINKING_CAPTURE_WAIT_MAX_MS))
  );
  const linkingStartReady = Boolean(
    hostedBridgeReady &&
      status?.localAvailable !== false &&
      !provisioningBlocked &&
      status?.screenshotUrl
  );
  const linkingInputWaitDismissed = Boolean(
    dismissedInputWaitAt && lastLinkingInputAt && dismissedInputWaitAt >= lastLinkingInputAt
  );
  const linkingInputWaitTimedOut = Boolean(
    linkingInputDeliveredWithoutState &&
      lastLinkingInputAt &&
      clockNow - lastLinkingInputAt >= LINKING_INPUT_WAIT_TIMEOUT_MS
  );
  const linkingOperationElapsedSeconds = Math.max(
    0,
    Math.floor((clockNow - (linkingOperationStartedAt || clockNow)) / 1000)
  );
  const linkingPreparationTimedOut = Boolean(
    linkingPreparing &&
      linkingOperationStartedAt &&
      clockNow - linkingOperationStartedAt >= LINKING_PREPARATION_TIMEOUT_MS
  );
  const linkingWaitModal = linkingWaitState({
    slotClaimPending: claimSlotMutation.isPending && !selectedSlot,
    startPending: startLinkingMutation.isPending,
    completePending: completeLinkingMutation.isPending,
    cancelPending: cancelLinkingMutation.isPending,
    preparationActive: linkingPreparing,
    preparationTimedOut: linkingPreparationTimedOut,
    inputPending: linkingCaptureBusy,
    inputDeliveredWithoutState: linkingInputDeliveredWithoutState && !linkingInputWaitDismissed,
    inputWaitTimedOut: linkingInputWaitTimedOut,
    inputKind: linkingInputKind,
    assistant: status?.linkingAssistant,
    hostedBridgeReady,
    localAvailable: status?.localAvailable,
    latestLogLine,
    t
  });
  const linkingOperationActive = Boolean(linkingWaitModal);
  const activity = dashboardActivity({
    status,
    commands,
    optimisticAction,
    commandPending: commandMutation.isPending,
    now: clockNow,
    fallbackLine: liveStatusLine,
    hideSecretBotLogs,
    unavailableTitle,
    unavailableMessage,
    t
  });
  const userActionModalView = userActionModal
    ? userActionModalPresentation(userActionModal, status, commands, clockNow, t)
    : null;
  const userActionModalActions = userActionModalView?.terminal
    ? [{ label: t('operation.close'), onClick: () => setUserActionModal(null), primary: true }]
    : [];
  const userActionModalActive = Boolean(
    userActionModal && userActionModal.phase !== 'success' && userActionModal.phase !== 'error' && userActionModal.phase !== 'timeout'
  );

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setUserActionModal((current) => advanceUserActionModal(current, status, commands, clockNow));
  }, [clockNow, commands, status]);

  useEffect(() => {
    if (!userActionModal || userActionModal.phase !== 'success') return;
    const id = window.setTimeout(() => setUserActionModal(null), USER_ACTION_SUCCESS_AUTO_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [userActionModal?.phase, userActionModal?.startedAt]);

  useEffect(() => {
    if (linkingOperationActive && !linkingOperationStartedAt) {
      setLinkingOperationStartedAt(Date.now());
      return;
    }
    if (!linkingOperationActive && linkingOperationStartedAt) {
      setLinkingOperationStartedAt(null);
    }
  }, [linkingOperationActive, linkingOperationStartedAt]);

  useEffect(() => {
    setLinkingEmailSent(false);
  }, [linkingSessionId]);

  useEffect(() => {
    if (!linkingChatSessionId || !api.subscribeLinkingChat) return;
    const unsubscribe = api.subscribeLinkingChat(linkingChatSessionId, (incoming) => {
      queryClient.setQueryData<LinkingChatMessage[]>(['linking-chat', linkingChatSessionId], (prev) => {
        const list = prev || [];
        if (list.some((existing) => existing.id === incoming.id)) return list;
        return [...list, incoming];
      });
    });
    return unsubscribe;
  }, [linkingChatSessionId, api, queryClient]);

  useEffect(() => {
    if (!linkingSessionId) {
      if (linkingReadySessionId) setLinkingReadySessionId(null);
      return;
    }
    if (linkingAutomationActive || linkingRecoveryBlocked || linkingFlowComplete) {
      if (linkingReadySessionId) setLinkingReadySessionId(null);
      return;
    }
    if (detectedLinkingAssistantReady && linkingReadySessionId !== linkingSessionId) {
      setLinkingReadySessionId(linkingSessionId);
      return;
    }
    if (linkingReadySessionId && linkingReadySessionId !== linkingSessionId) {
      setLinkingReadySessionId(null);
    }
  }, [
    detectedLinkingAssistantReady,
    linkingAutomationActive,
    linkingFlowComplete,
    linkingReadySessionId,
    linkingRecoveryBlocked,
    linkingSessionId
  ]);

  useEffect(() => {
    if (!linkingSession || !linkingFlowComplete || !linkingSessionId) return;
    if (completeLinkingMutation.isPending || autoCompletingLinkingSessionId === linkingSessionId) return;
    setAutoCompletingLinkingSessionId(linkingSessionId);
    completeLinkingMutation.mutate(linkingSession);
  }, [
    autoCompletingLinkingSessionId,
    completeLinkingMutation,
    linkingFlowComplete,
    linkingSession,
    linkingSessionId
  ]);

  // AUTO-CLAIM DE SLOT HOSTED — DESACTIVADO (pivote 2026-07: el hosted es beta
  // privada provisionada por el admin, no auto-alta). Se conserva comentado para
  // REACTIVARLO cuando el hosted reabra el auto-alta. En su lugar, un usuario que
  // llega al dashboard hosted SIN slot no es un usuario hosted: se le manda al
  // producto descargable (ver el efecto de redirección de abajo).
  // useEffect(() => {
  //   if (api.mode !== 'supabase' || !api.claimNextAvailableSlot) return;
  //   if (userQuery.data?.role !== 'user') return;
  //   if (!slotsQuery.isSuccess || slotsQuery.error || slots.length > 0) return;
  //   if (claimSlotMutation.isPending) return;
  //   if (lastSlotClaimAttemptAt && clockNow - lastSlotClaimAttemptAt < SLOT_CLAIM_RETRY_MS) return;
  //   claimSlotMutation.mutate();
  // }, [
  //   api.mode,
  //   api.claimNextAvailableSlot,
  //   userQuery.data?.role,
  //   slotsQuery.isSuccess,
  //   slotsQuery.error,
  //   slots.length,
  //   claimSlotMutation.isPending,
  //   lastSlotClaimAttemptAt,
  //   clockNow
  // ]);

  useEffect(() => {
    if (api.mode !== 'supabase') return;
    if (userQuery.data?.role !== 'user') return;
    // Espera a que la lista de slots esté cargada de verdad; solo redirige si el
    // usuario NO tiene ningún slot hosted (no es un usuario de la beta hosted).
    if (!slotsQuery.isSuccess || slotsQuery.error || slots.length > 0) return;
    window.location.href = DOWNLOADABLE_APP_URL;
  }, [api.mode, userQuery.data?.role, slotsQuery.isSuccess, slotsQuery.error, slots.length]);

  useEffect(() => {
    if (!lastLinkingInputAt || sendLinkInputMutation.isPending) return;
    const screenshotChanged = Boolean(
      status?.screenshotUrl && status.screenshotUrl !== lastLinkingInputScreenshotUrl
    );
    if (
      !linkingSessionId ||
      (statusQuery.dataUpdatedAt > lastLinkingInputAt + 250 && screenshotChanged) ||
      clockNow - lastLinkingInputAt >= LINKING_CAPTURE_WAIT_MAX_MS
    ) {
      setLastLinkingInputAt(null);
      setLastLinkingInputScreenshotUrl(null);
    }
  }, [
    clockNow,
    lastLinkingInputAt,
    lastLinkingInputScreenshotUrl,
    linkingSessionId,
    sendLinkInputMutation.isPending,
    status?.screenshotUrl,
    statusQuery.dataUpdatedAt
  ]);

  useEffect(() => {
    if (!tutorialKey || userQuery.data?.role === 'admin') {
      setTutorialDismissed(true);
      return;
    }
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(tutorialKey) === 'dismissed';
    } catch {
      dismissed = false;
    }
    setTutorialDoNotShow(false);
    setTutorialDismissed(dismissed);
  }, [tutorialKey, userQuery.data?.role]);

  useEffect(() => {
    const licStatus = selectedSlot?.licenseStatus || 'inactive';
    const planKey = String(selectedSlot?.planKey || '').toLowerCase();
    let licLabel: string = licStatus;
    if (licStatus === 'active') {
      licLabel = planKey.includes('plus') ? 'pro+' : 'pro';
    }
    onSlotStateChange?.({
      license: licLabel,
      linked: Boolean(selectedSlot?.linked),
      licenseDetail: selectedSlot ? licenseDetail(selectedSlot.licenseStatus, selectedSlot.trialEndsAt) : '',
      isStripe: selectedSlot?.licenseProvider === 'stripe'
    });
  }, [onSlotStateChange, selectedSlot]);

  useEffect(() => {
    if (!settingsKey) return;
    let saved: unknown = null;
    try {
      const raw = window.localStorage.getItem(settingsKey);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      saved = null;
    }
    skipSettingsSaveForKey.current = settingsKey;
    setSettings(mergeDashboardSettings(saved));
  }, [settingsKey]);

  useEffect(() => {
    if (!settingsKey || !api.getSlotSettings || !slotSettingsQuery.isSuccess || !slotSettingsQuery.data) return;
    setSettings(mergeDashboardSettings(slotSettingsQuery.data));
  }, [api.getSlotSettings, settingsKey, slotSettingsQuery.data, slotSettingsQuery.isSuccess]);

  useEffect(() => {
    if (!settingsKey) return;
    if (skipSettingsSaveForKey.current === settingsKey) {
      skipSettingsSaveForKey.current = null;
      return;
    }
    window.localStorage.setItem(settingsKey, JSON.stringify(settings));
    if (!api.saveSlotSettings || !selectedSlot?.id) return;
    if (remoteSettingsSaveTimer.current) window.clearTimeout(remoteSettingsSaveTimer.current);
    const slotId = selectedSlot.id;
    remoteSettingsSaveTimer.current = window.setTimeout(() => {
      api.saveSlotSettings?.(slotId, settings).catch((error) => setMessage(formatError(error)));
    }, 700);
    return () => {
      if (remoteSettingsSaveTimer.current) window.clearTimeout(remoteSettingsSaveTimer.current);
    };
  }, [api, selectedSlot?.id, settingsKey, settings]);

  useEffect(() => {
    if (!status?.reconnectSettings) return;
    setReconnectSettings(status.reconnectSettings);
  }, [status?.reconnectSettings]);

  useEffect(() => {
    if (!optimisticAction) return;
    const hasRelatedCommand = commands.some(
      (command) =>
        command.action === optimisticAction.action &&
        (command.status === 'queued' || command.status === 'delivered')
    );
    if (status?.running || hasRelatedCommand) return;
    if (clockNow - optimisticAction.startedAt > 45000) {
      setOptimisticAction(null);
    }
  }, [clockNow, commands, optimisticAction, status?.running]);

  function updateReconnectSettings(next: ReconnectSettings) {
    const normalized = {
      autoEnabled: next.autoEnabled,
      delaySeconds: Math.max(30, Math.min(3600, Math.round(next.delaySeconds || 300)))
    };
    setReconnectSettings(normalized);
    if (api.saveReconnectSettings && selectedSlot?.id) {
      reconnectSettingsMutation.mutate(normalized);
    }
  }

  function requestSlotClaim() {
    if (!api.claimNextAvailableSlot || claimSlotMutation.isPending) return;
    setLastSlotClaimAttemptAt(null);
    claimSlotMutation.mutate();
  }

  useEffect(() => {
    if (!onReconnectMenuChange) return;
    if (!selectedSlot || (!api.saveReconnectSettings && !api.reconnectNow)) {
      if (reconnectMenuSignature.current !== 'none') {
        reconnectMenuSignature.current = 'none';
        onReconnectMenuChange(null);
      }
      return;
    }

    const running = Boolean(status?.running || status?.reconnect?.running);
    const canSave = Boolean(api.saveReconnectSettings && selectedSlot);
    const canReconnect = Boolean(api.reconnectNow && selectedSlot);
    const signature = [
      selectedSlot.id,
      reconnectSettings.autoEnabled ? '1' : '0',
      reconnectSettings.delaySeconds,
      running ? '1' : '0',
      canSave ? '1' : '0',
      canReconnect ? '1' : '0'
    ].join(':');
    if (reconnectMenuSignature.current === signature) return;
    reconnectMenuSignature.current = signature;
    onReconnectMenuChange({
      settings: reconnectSettings,
      running,
      canSave,
      canReconnect,
      onSettingsChange: updateReconnectSettings,
      onReconnectNow: startReconnect
    });
  }, [
    api.reconnectNow,
    api.saveReconnectSettings,
    onReconnectMenuChange,
    reconnectSettings,
    reconnectNowMutation,
    selectedSlot,
    status?.reconnect?.running,
    status?.running
  ]);

  useEffect(() => {
    return () => onReconnectMenuChange?.(null);
  }, [onReconnectMenuChange]);

  function closeTutorial(permanent: boolean) {
    if (permanent && tutorialKey) {
      try {
        window.localStorage.setItem(tutorialKey, 'dismissed');
      } catch {
        // Non-critical: the tutorial can appear again in this browser.
      }
    }
    setTutorialDismissed(true);
  }

  function sendAction(action: ActionKey) {
    setOptimisticAction({ action, startedAt: Date.now() });
    setUserActionModal({
      action,
      startedAt: Date.now(),
      phase: 'sending',
      sawCommand: false,
      sawWorker: false,
      commandId: null,
      error: null
    });
    commandMutation.mutate(action);
  }

  return (
    <main className="dashboard-grid">
      {!tutorialDismissed && (
        <OnboardingTutorialModal
          doNotShow={tutorialDoNotShow}
          onToggleDoNotShow={setTutorialDoNotShow}
          onClose={() => closeTutorial(tutorialDoNotShow)}
          onConfirm={() => closeTutorial(true)}
        />
      )}
      {quotaModalOpen && (
        <IntelligenceQuotaModal
          onClose={() => setQuotaModalOpen(false)}
        />
      )}
      {linkingModal && (
        <LinkingInfoModal
          title={linkingModal.title}
          body={linkingModal.body}
          onClose={() => setLinkingModal(null)}
        />
      )}
      {reconnectModal && (
        <ReconnectWaitModal
          kind={reconnectModal.kind}
          reason={reconnectModal.reason}
          slow={Boolean(reconnectModal.slow)}
          canRetry={Boolean(api.reconnectNow && selectedSlot)}
          onRetry={startReconnect}
          onClose={() => setReconnectModal(null)}
        />
      )}
      {userActionModalView && (
        <OperationModal
          active={userActionModalView.active}
          tone={userActionModalView.tone}
          titleId="user-action-operation-title"
          eyebrow={userActionModalView.eyebrow}
          title={userActionModalView.title}
          body={userActionModalView.body}
          detail={userActionModalView.detail}
          error={userActionModalView.error}
          code={userActionModalView.code}
          statusItems={userActionModalView.statusItems}
          actions={userActionModalActions}
        />
      )}
      {linkingWaitModal && (
        <LinkingWaitModal
          title={linkingWaitModal.title}
          body={linkingWaitModal.body}
          status={linkingWaitModal.status}
          detail={linkingWaitModal.detail}
          elapsedSeconds={linkingOperationElapsedSeconds}
          active={!linkingWaitModal.failed}
          actions={
            linkingWaitModal.retryable
              ? [
                  { label: t('linking.inputStalledRetry'), onClick: () => setDismissedInputWaitAt(Date.now()) },
                  ...(linkingSession
                    ? [
                        {
                          label: t('linking.cancel'),
                          onClick: () => cancelLinkingMutation.mutate({ session: linkingSession }),
                          disabled: cancelLinkingMutation.isPending
                        }
                      ]
                    : [])
                ]
              : linkingWaitModal.failed && linkingSession
                ? [
                    {
                      label: t('linking.cancel'),
                      onClick: () => cancelLinkingMutation.mutate({ session: linkingSession }),
                      disabled: cancelLinkingMutation.isPending
                    }
                  ]
                : []
          }
        />
      )}
      <div className="left-stack">
        {userQuery.error && <ErrorState error={userQuery.error} />}
        {slotsQuery.error && <ErrorState error={slotsQuery.error} />}

        {/* PANEL DE CAPACIDAD/CLAIM DE SLOT — DESACTIVADO junto con el auto-claim
            (hosted = beta privada provisionada por admin). Un usuario sin slot se
            redirige al descargable (ver efecto arriba), así que no se muestra este
            panel. Se conserva para REACTIVARLO cuando el hosted reabra el auto-alta.
        {!selectedSlot && !slotsQuery.isLoading && !isAdminUser && api.claimNextAvailableSlot && (
          <SlotCapacityPanel
            state={slotClaimState}
            busy={claimSlotMutation.isPending}
            onRetry={requestSlotClaim}
          />
        )} */}

        {selectedSlot && !selectedSlot.linked && api.startLinkingSession && (
          <LinkingPanel
            session={linkingSession}
            message={
              linkingSession
                ? linkingInputReady
                  ? linkingInputKind === 'password'
                    ? t('linking.passwordMessage')
                    : t('linking.activeMessage')
                  : t('linking.preparingMessage')
                : t('linking.startMessage')
            }
            text={linkText}
            textVisible={linkTextVisible}
            sessionBusy={
              startLinkingMutation.isPending ||
              completeLinkingMutation.isPending ||
              cancelLinkingMutation.isPending
            }
            inputBusy={sendLinkInputMutation.isPending}
            captureBusy={linkingCaptureBusy}
            inputReady={linkingInputReady}
            inputKind={linkingInputKind}
            now={clockNow}
            onStart={() => {
              if (!linkingStartReady) {
                setLinkingModal({ title: t('linking.notReadyTitle'), body: t('linking.notReadyBody') });
                return;
              }
              startLinkingMutation.mutate();
            }}
            onTextChange={setLinkText}
            onToggleTextVisible={() => setLinkTextVisible((visible) => !visible)}
            onSendText={() => {
              if (!linkingSession || !linkText.trim()) return;
              if (linkingInputKind === 'email') setLinkingEmailSent(true);
              setLastLinkingInputAt(Date.now());
              setLastLinkingInputScreenshotUrl(status?.screenshotUrl || null);
              sendLinkInputMutation.mutate({
                session: linkingSession,
                eventType: 'text',
                payload: { text: linkText.trim(), kind: linkingInputKind }
              });
              setLinkText('');
              setLinkTextVisible(false);
            }}
            onCancel={() => linkingSession && cancelLinkingMutation.mutate({ session: linkingSession })}
            assistanceSupported={Boolean(api.requestLinkingAssistance && api.sendLinkingChatMessage)}
            assistanceRequested={linkingAssistanceRequested}
            assistanceBusy={requestAssistanceMutation.isPending}
            operatorActive={linkingOperatorActive}
            chatMessages={linkingChatMessages}
            chatBusy={sendChatMutation.isPending}
            onRequestAssistance={() => linkingSession && requestAssistanceMutation.mutate(linkingSession.id)}
            onSendChat={(body) => linkingSession && sendChatMutation.mutate({ sessionId: linkingSession.id, body })}
          />
        )}

        <Panel title={t('dashboard.actions')}>
          <BotStatusInfoBox activity={activity} />
          <ActionTimingStrip timing={liveTiming} />
          {!api.allowCommands && (
            <div className="notice">
              {t('dashboard.readOnly')}
            </div>
          )}
          {api.allowCommands && slotCommandAllowed && !hostedBridgeReady && (
            <div className="notice">{unavailableMessage}</div>
          )}
          {api.allowCommands && slotCommandAllowed && hostedBridgeReady && status?.provisioning?.status === 'warning' && (
            <div className="notice">
              {t('dashboard.provisioningReview')}: {status.provisioning.detail || status.provisioning.label}
            </div>
          )}
          {api.allowCommands && slotCommandAllowed && hostedBridgeReady && provisioningBlocked && (
            <div className="notice warning">
              {t('dashboard.slotNotOperational')}: {status?.provisioning?.detail || status?.provisioning?.label}
            </div>
          )}
          {linkingSession && (
            <div className="notice warning">{t('linking.actionsBlockedNotice')}</div>
          )}
          <div className="action-list">
            {actions.map((action) => {
              const label = t(action.labelKey);
              const availability = actionAvailability(selectedSlot, status, commands, action.key);
              const actionBlockedByProvisioning = provisioningBlocked && action.key !== 'stop';
              return (
                <button
                  key={action.key}
                  className={action.tone}
                disabled={
                  !api.allowCommands ||
                  Boolean(linkingSession) ||
                  !onboarding.canRunActions ||
                  !slotCommandAllowed ||
                  !hostedBridgeReady ||
                    actionBlockedByProvisioning ||
                    !availability.allowed ||
                    commandMutation.isPending ||
                    userActionModalActive
                  }
                  title={
                    !api.allowCommands
                      ? 'Comandos reales desactivados'
                      : linkingSession
                        ? t('linking.actionsBlockedTitle')
                      : !onboarding.canRunActions
                        ? onboarding.title
                      : !slotCommandAllowed
                        ? 'Selecciona un slot'
                      : !hostedBridgeReady
                          ? unavailableMessage
                          : actionBlockedByProvisioning
                            ? 'Slot no operativo'
                            : availability.reason || label
                  }
                  onClick={() => sendAction(action.key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {activity.reason && <div className="activity-reason">{activity.reason}</div>}
          {commandsQuery.error && (
            <div className="danger-text">{t('dashboard.commandsReadError')}</div>
          )}
          {message && <div className="muted">{message}</div>}
        </Panel>

        {status?.screenBlocker && (
          <ReconnectPanel
            blocked={Boolean(status?.screenBlocker)}
            reconnect={status?.reconnect}
            settings={reconnectSettings}
            running={Boolean(status?.running || status?.reconnect?.running)}
            canSave={Boolean(api.saveReconnectSettings && selectedSlot)}
            canReconnect={Boolean(api.reconnectNow && selectedSlot && status?.screenBlocker)}
            onSettingsChange={updateReconnectSettings}
            onReconnectNow={startReconnect}
          />
        )}

        {onboarding.canRunActions && (
          <ActionSettingsPanel settings={settings} onChange={setSettings} autoShieldAllowed={autoShieldAllowed} />
        )}

        {intelligenceAllowed && api.listIntelligenceRequests && api.submitIntelligenceRequest && (
          <IntelligencePanel
            requests={visibleIntelligenceRequests}
            quota={intelligenceQuota}
            loading={intelligenceQuery.isLoading}
            error={intelligenceQuery.error}
            busy={intelligenceMutation.isPending}
            deletingId={deleteIntelligenceMutation.variables}
            canDelete={Boolean(api.deleteIntelligenceRequest)}
            defaultRegionId={selectedSlot.regionId || selectedSlot.detectedRegionId || ''}
            onSubmit={(request) => intelligenceMutation.mutate(request)}
            onDelete={(requestId) => deleteIntelligenceMutation.mutate(requestId)}
          />
        )}
      </div>

      <div className="center-stack">
        <Panel title={t('dashboard.screenshot')}>
          <ScreenshotFrame
            src={linkingPreparing ? null : status?.screenshotUrl}
            state={status?.state || selectedSlot?.state || 'idle'}
            linkingActive={Boolean(linkingSession && linkingInputReady)}
            waitMessage={linkingCaptureBusy ? t('linking.waitOverlay') : null}
            waitHint={linkingCaptureBusy ? t('linking.waitOverlayHint') : null}
            onTap={(xRatio, yRatio) => {
              if (!linkingSession || !linkingInputReady) return;
              setLastLinkingInputAt(Date.now());
              setLastLinkingInputScreenshotUrl(status?.screenshotUrl || null);
              sendLinkInputMutation.mutate({
                session: linkingSession,
                eventType: 'tap',
                payload: { x_ratio: xRatio, y_ratio: yRatio }
              });
            }}
          />
        </Panel>
      </div>

      <div className="right-stack">
        <QueuePanel title={t('dashboard.colony')} queues={status?.colonyQueues || []} />
        <QueuePanel title={t('dashboard.world')} queues={status?.worldQueues || []} />
      </div>
    </main>
  );
}

function cleanLogLine(value?: string | null, hideSecret = true): string {
  const line = String(value || '').trim();
  if (!line || line === '-' || line.toLowerCase() === 'null' || line.toLowerCase() === 'undefined') return '';
  if (hideSecret && isSecretBotLogLine(line)) return '';
  if (hideSecret && isNoisyScreenBlockerLogLine(line)) return '';
  if (hideSecret && isInternalOperationalLogLine(line)) return '';
  return line;
}

function linkingAssistantPhase(assistant?: { phase?: string | null } | null): string {
  return String(assistant?.phase || '').toLowerCase();
}

function isGoogleEmailPhase(phase: string): boolean {
  return phase === 'google_email_ready' || phase === 'google_email_focused';
}

function isGooglePasswordPhase(phase: string): boolean {
  return phase === 'google_password_ready' || phase === 'google_password_focused';
}

function isLinkingAssistantAutomationActive(assistant?: { phase?: string | null } | null): boolean {
  const phase = linkingAssistantPhase(assistant);
  return [
    'launching_google_play',
    'focusing_google_email',
    'submitting_google_email',
    'waiting_google_password',
    'submitting_google_password',
    'google_terms_accepting',
    'google_contacts_skipping',
    'google_recovery_reviewing',
    'google_services_configuring'
  ].includes(phase);
}

function isLinkingAssistantRecoveryBlocked(assistant?: { phase?: string | null; ok?: boolean | null } | null): boolean {
  return linkingAssistantPhase(assistant) === 'google_recovery_missing' || assistant?.ok === false;
}

function isLinkingAssistantComplete(assistant?: { phase?: string | null; ok?: boolean | null } | null): boolean {
  return assistant?.ok !== false && linkingAssistantPhase(assistant) === 'google_linking_complete';
}

function isLinkingInputLog(latestLogLine?: string | null): boolean {
  return /linking input:\s*(text|tap|key)\b/i.test(String(latestLogLine || ''));
}

function isLinkingAssistantReady(
  assistant?: { phase?: string | null; ok?: boolean | null; reason?: string | null } | null,
  latestLogLine?: string | null
): boolean {
  const phase = String(assistant?.phase || '').toLowerCase();
  if (isGoogleEmailPhase(phase) || isGooglePasswordPhase(phase)) {
    return true;
  }
  if (assistant?.ok === true && /google_(email|password)_(ready|focused)/i.test(String(assistant?.reason || ''))) {
    return true;
  }
  return /linking assistant: .* completed phase=google_(email|password)_(ready|focused)/i.test(
    String(latestLogLine || '')
  );
}

function linkingWaitState({
  slotClaimPending,
  startPending,
  completePending,
  cancelPending,
  preparationActive,
  preparationTimedOut,
  inputPending,
  inputDeliveredWithoutState,
  inputWaitTimedOut,
  inputKind,
  assistant,
  hostedBridgeReady,
  localAvailable,
  latestLogLine,
  t
}: {
  slotClaimPending: boolean;
  startPending: boolean;
  completePending: boolean;
  cancelPending: boolean;
  preparationActive: boolean;
  preparationTimedOut: boolean;
  inputPending: boolean;
  inputDeliveredWithoutState: boolean;
  inputWaitTimedOut: boolean;
  inputKind: 'email' | 'password';
  assistant?: { phase?: string | null; action?: string | null; reason?: string | null; ok?: boolean | null } | null;
  hostedBridgeReady: boolean;
  localAvailable?: boolean | null;
  latestLogLine?: string | null;
  t: (key: TranslationKey) => string;
}): { title: string; body: string; status: string; detail?: string | null; failed?: boolean; retryable?: boolean } | null {
  if (slotClaimPending) {
    return { title: t('linking.claimingSlotTitle'), body: t('linking.claimingSlotBody'), status: t('linking.prepareStatusClaiming') };
  }
  if (startPending) {
    return { title: t('linking.waitStartingTitle'), body: t('linking.waitStartingBody'), status: t('linking.prepareStatusStarting') };
  }
  if (completePending) {
    return { title: t('linking.waitCompleteTitle'), body: t('linking.waitCompleteBody'), status: t('linking.prepareStatusCompleting') };
  }
  if (cancelPending) {
    return { title: t('linking.waitCancelTitle'), body: t('linking.waitCancelBody'), status: t('linking.prepareStatusCancelling') };
  }
  const phase = linkingAssistantPhase(assistant);
  if (phase === 'google_recovery_missing' || assistant?.ok === false) {
    return {
      title: t('linking.recoveryMissingTitle'),
      body: t('linking.recoveryMissingBody'),
      status: t('linking.prepareStatusFailed'),
      detail: assistant?.reason || latestLogLine || null,
      failed: true
    };
  }
  if (phase === 'google_linking_complete') {
    return {
      title: t('linking.waitCompleteTitle'),
      body: t('linking.waitCompleteBody'),
      status: t('linking.prepareStatusCompleting'),
      detail: assistant?.reason || latestLogLine || null
    };
  }
  if (isLinkingAssistantAutomationActive(assistant)) {
    if (phase === 'launching_google_play' || phase === 'focusing_google_email') {
      return {
        title: t('linking.prepareTitle'),
        body: t('linking.prepareOpeningGoogleBody'),
        status: t('linking.prepareStatusOpeningGoogle'),
        detail: assistant?.reason || latestLogLine || null
      };
    }
    if (phase === 'submitting_google_email' || phase === 'waiting_google_password') {
      return {
        title: t('linking.waitEmailTitle'),
        body: t('linking.waitEmailBody'),
        status: t('linking.prepareStatusOpeningGoogle'),
        detail: assistant?.reason || latestLogLine || null
      };
    }
    return {
      title: t('linking.waitPasswordTitle'),
      body: t('linking.waitPasswordBody'),
      status: t('linking.prepareStatusCompleting'),
      detail: assistant?.reason || latestLogLine || null
    };
  }
  if (inputPending) {
    return {
      title: t('linking.waitInputTitle'),
      body: inputKind === 'password' ? t('linking.waitPasswordInputBody') : t('linking.waitInputBody'),
      status: t('linking.prepareStatusCapture'),
      detail: latestLogLine || null
    };
  }
  if (inputDeliveredWithoutState) {
    if (inputWaitTimedOut) {
      return {
        title: t('linking.inputStalledTitle'),
        body: t('linking.inputStalledBody'),
        status: t('linking.prepareStatusFailed'),
        detail: latestLogLine || null,
        failed: true,
        retryable: true
      };
    }
    return {
      title: t('linking.waitInputTitle'),
      body: inputKind === 'password' ? t('linking.waitPasswordInputBody') : t('linking.waitInputBody'),
      status: t('linking.prepareStatusCapture'),
      detail: latestLogLine || null
    };
  }
  if (preparationActive) {
    if (preparationTimedOut) {
      return {
        title: t('linking.prepareFailedTitle'),
        body: t('linking.prepareFailedBody'),
        status: t('linking.prepareStatusFailed'),
        detail: assistant?.reason || latestLogLine || null,
        failed: true
      };
    }
    if (!hostedBridgeReady || localAvailable === false) {
      return {
        title: t('linking.prepareTitle'),
        body: t('linking.prepareWorkerBody'),
        status: t('linking.prepareStatusWorker'),
        detail: latestLogLine || assistant?.reason || null
      };
    }
    if (
      phase === 'opening_google' ||
      phase === 'launching_google_play' ||
      phase === 'google_play_sign_in' ||
      phase === 'google_credentials' ||
      phase === 'google_email_ready' ||
      phase === 'focusing_google_email' ||
      assistant?.action === 'google_play_email_focus_flow'
    ) {
      return {
        title: t('linking.prepareTitle'),
        body: t('linking.prepareOpeningGoogleBody'),
        status: t('linking.prepareStatusOpeningGoogle'),
        detail: assistant?.reason || null
      };
    }
    if (phase === 'navigating' || assistant?.action) {
      return {
        title: t('linking.prepareTitle'),
        body: t('linking.prepareNavigatingBody'),
        status: t('linking.prepareStatusNavigating'),
        detail: assistant?.reason || assistant?.action || latestLogLine || null
      };
    }
    return {
      title: t('linking.prepareTitle'),
      body: t('linking.prepareCaptureBody'),
      status: t('linking.prepareStatusCapture'),
      detail: latestLogLine || assistant?.reason || null
    };
  }
  return null;
}

function isSecretBotLogLine(line: string): boolean {
  return /intelligence[_\s-]/i.test(line) || /\btarget_(saved|persist|skipped|detected|validating)\b/i.test(line);
}

function isNoisyScreenBlockerLogLine(line: string): boolean {
  return /\[BLOCKED\]\s+Screen blocker detected/i.test(line) || /screen_blocker_detected/i.test(line);
}

function isInternalOperationalLogLine(line: string): boolean {
  return (
    /\bbridge\b|\bheartbeat\b|\bworker\b/i.test(line) ||
    /127\.0\.0\.1|localhost|\/api\/agent\/status/i.test(line) ||
    /could not reach|connection refused|local_status|pre-click ui check/i.test(line)
  );
}

function isLinkingRateLimitError(error: unknown): boolean {
  return /too many .*inputs|please wait a moment|rate limit/i.test(formatError(error));
}

function isLinkingExpiredError(error: unknown): boolean {
  return /linking session expired|active linking session not found/i.test(formatError(error));
}

function isIntelligenceQuotaError(error: unknown): boolean {
  return /daily intelligence quota exceeded/i.test(formatError(error));
}

function isIntelligenceAllowed(slot: BotSlot | null): boolean {
  return intelligenceDailyQuota(slot) > 0;
}

function isAutoShieldAllowed(slot: BotSlot | null): boolean {
  if (!slot?.linked) return false;
  const plan = intelligenceCommercialPlan(slot);
  return plan === 'pro_plus' || plan === 'trial';
}

function intelligenceDailyQuota(slot: BotSlot | null): number {
  if (!slot?.linked) return 0;
  const plan = intelligenceCommercialPlan(slot);
  if (plan === 'trial' || plan === 'pro_plus') return 10;
  if (plan === 'pro') return 1;
  return 0;
}

function intelligenceCommercialPlan(slot: BotSlot): 'trial' | 'pro' | 'pro_plus' | null {
  if (slot.licenseStatus === 'trial') return 'trial';
  if (slot.licenseStatus !== 'active') return null;
  const planKey = String(slot.planKey || '').toLowerCase();
  if (isProPlusPlan(planKey)) return 'pro_plus';
  if (isProPlan(planKey)) return 'pro';
  if (!planKey && slot.licensePlan === 'pro_plus') return 'pro_plus';
  if (!planKey && slot.licensePlan === 'pro') return 'pro';
  return null;
}

function isProPlan(planKey: string): boolean {
  return ['pro', 'pro_monthly', 'pro-monthly', 'promonthly'].includes(planKey);
}

function isProPlusPlan(planKey: string): boolean {
  return ['pro_plus', 'pro+', 'pro-plus', 'proplus'].includes(planKey);
}

function intelligenceQuotaState(slot: BotSlot | null, requests: IntelligenceRequest[]) {
  const limit = intelligenceDailyQuota(slot);
  const dayStart = utcDayStartMs(Date.now());
  const used = requests.filter((request) => {
    if (request.status === 'failed') return false;
    const parsed = Date.parse(request.createdAt);
    return Number.isFinite(parsed) && parsed >= dayStart;
  }).length;
  return {
    limit,
    used: Math.min(used, limit || used),
    remaining: Math.max(0, limit - used)
  };
}

function utcDayStartMs(nowMs: number): number {
  const date = new Date(nowMs);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function licenseDetail(status: string, trialEndsAt?: string | null): string {
  if (status !== 'trial') return '';
  if (!trialEndsAt) return 'Trial sin fecha de fin configurada';
  const endsAt = new Date(trialEndsAt);
  if (Number.isNaN(endsAt.getTime())) return '';
  const remainingMs = endsAt.getTime() - Date.now();
  if (remainingMs <= 0) return 'Trial caducado';
  const days = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  return `Trial: ${days} dia${days === 1 ? '' : 's'} restantes`;
}

function liveActionTiming(status: BotStatus | null, now: number, fallbackUpdatedAt: number) {
  const nextStartedAt = status?.nextCheckStartedAt ? new Date(status.nextCheckStartedAt).getTime() : fallbackUpdatedAt;
  const nextElapsed =
    nextStartedAt && !Number.isNaN(nextStartedAt) ? Math.max(0, Math.floor((now - nextStartedAt) / 1000)) : 0;
  const runtimeStartedAt = status?.runtimeStartedAt ? new Date(status.runtimeStartedAt).getTime() : null;
  const runtimeBase = Math.max(0, Number(status?.runtimeSeconds || 0));
  const runtimeSeconds =
    status?.running && runtimeStartedAt && !Number.isNaN(runtimeStartedAt)
      ? Math.max(0, Math.floor((now - runtimeStartedAt) / 1000))
      : status?.running
        ? runtimeBase
        : 0;
  const nextBase =
    typeof status?.nextCheckSeconds === 'number' && Number.isFinite(status.nextCheckSeconds)
      ? status.nextCheckSeconds
      : null;

  return {
    runtimeSeconds,
    nextSeconds: nextBase == null ? null : Math.max(0, nextBase - nextElapsed)
  };
}

type DashboardActivity = {
  kind: 'idle' | 'sending' | 'queued' | 'running' | 'waiting' | 'blocked' | 'offline';
  title: string;
  detail: string;
  reason?: string;
  action?: ActionKey;
  showOverlay: boolean;
};

function actionLabel(action: ActionKey | undefined, t: (key: TranslationKey) => string): string {
  if (action === 'auto_bot') return t('dashboard.autoBot');
  if (action === 'collect') return t('dashboard.collect');
  if (action === 'train_troops') return t('dashboard.trainTroops');
  if (action === 'auto_hunt') return t('dashboard.autoHunt');
  if (action === 'stop') return t('dashboard.stopBot');
  if (action === 'reconnect') return t('dashboard.reconnect');
  return t('dashboard.actions');
}

function stateActivityTitle(status: BotStatus | null, t: (key: TranslationKey) => string): string {
  const state = String(status?.state || '').toLowerCase();
  const line = cleanLogLine(status?.latestLogLine, true).toLowerCase();
  if (status?.reconnect?.running) return t('activity.reconnecting');
  if (state.includes('startup') || state.includes('verifying') || line.includes('startup check')) return t('activity.starting');
  if (state.includes('auto_bot')) return t('activity.autoBotRunning');
  if (state.includes('training')) return t('activity.training');
  if (state.includes('hunting')) return t('activity.hunting');
  if (state.includes('collecting') || state === 'running') return t('activity.working');
  if (state.includes('stopping')) return t('activity.stopping');
  return t('activity.working');
}

function pendingCommand(commands: BotCommand[]): BotCommand | null {
  return commands.find((command) => command.status === 'queued' || command.status === 'delivered') || null;
}

const userActionPendingStatuses = new Set<BotCommand['status']>(['queued', 'delivered']);
const userActionFailureStatuses = new Set<BotCommand['status']>(['failed', 'rejected', 'cancelled']);
const userActionBusyStates = new Set(['collecting', 'training', 'hunting', 'auto_bot', 'running', 'stopping']);
const userActionTerminalPhases = new Set<UserActionModalPhase>(['success', 'error', 'timeout']);

function isUserActionFailureCommandStatus(status: BotCommand['status']): boolean {
  return userActionFailureStatuses.has(status);
}

function userActionPhaseFromCommandStatus(status: BotCommand['status']): UserActionModalPhase {
  if (isUserActionFailureCommandStatus(status)) return 'error';
  if (status === 'completed' || status === 'duplicate') return 'success';
  if (status === 'delivered') return 'delivered';
  return 'queued';
}

function isUserActionTerminal(phase: UserActionModalPhase): boolean {
  return userActionTerminalPhases.has(phase);
}

function relevantUserActionCommand(current: UserActionModalState, commands: BotCommand[]): BotCommand | null {
  if (current.commandId) {
    const byId = commands.find((command) => command.commandId === current.commandId);
    if (byId) return byId;
  }
  return commands.find((command) => command.action === current.action && userActionPendingStatuses.has(command.status)) || null;
}

function statusLooksBusyForUserAction(status: BotStatus | null): boolean {
  const state = String(status?.state || '').toLowerCase();
  return Boolean(status?.running || status?.reconnect?.running || userActionBusyStates.has(state));
}

function statusLooksReadyAfterStop(status: BotStatus | null): boolean {
  return !statusLooksBusyForUserAction(status);
}

function advanceUserActionModal(
  current: UserActionModalState | null,
  status: BotStatus | null,
  commands: BotCommand[],
  now: number
): UserActionModalState | null {
  if (!current || isUserActionTerminal(current.phase)) return current;

  const elapsed = now - current.startedAt;
  if (elapsed > USER_ACTION_MODAL_TIMEOUT_MS) {
    return { ...current, phase: 'timeout' };
  }

  const command = relevantUserActionCommand(current, commands);
  const commandStatus = command?.status;
  if (command && isUserActionFailureCommandStatus(command.status)) {
    return { ...current, commandId: command.commandId, phase: 'error', error: command.error || command.status };
  }
  if (command && command.status === 'duplicate') {
    return { ...current, commandId: command.commandId, phase: 'success', sawCommand: true };
  }
  if (command && command.status === 'completed') {
    return { ...current, commandId: command.commandId, phase: 'success', sawCommand: true };
  }

  const pending = commandStatus === 'queued' || commandStatus === 'delivered';
  const busy = statusLooksBusyForUserAction(status);
  const sawCommand = current.sawCommand || Boolean(command);
  const sawWorker = current.sawWorker || busy;

  if (current.action !== 'stop' && status?.screenBlocker?.kind && sawCommand) {
    return { ...current, phase: 'error', sawCommand, sawWorker, error: 'blocked' };
  }

  if (current.action === 'stop') {
    if (sawCommand && !pending && statusLooksReadyAfterStop(status)) {
      return { ...current, phase: 'success', sawCommand, sawWorker };
    }
    if (busy) {
      return { ...current, phase: 'running', sawCommand, sawWorker };
    }
    if (command && command.status === 'delivered') {
      return { ...current, commandId: command.commandId, phase: 'delivered', sawCommand, sawWorker };
    }
    if (command && command.status === 'queued') {
      return { ...current, commandId: command.commandId, phase: 'queued', sawCommand, sawWorker };
    }
    return { ...current, sawCommand, sawWorker };
  }

  if (busy && sawCommand) {
    return { ...current, phase: 'success', sawCommand, sawWorker };
  }
  if (command && command.status === 'delivered') {
    return { ...current, commandId: command.commandId, phase: 'delivered', sawCommand, sawWorker };
  }
  if (command && command.status === 'queued') {
    return { ...current, commandId: command.commandId, phase: 'queued', sawCommand, sawWorker };
  }
  if (sawCommand && !pending && elapsed > 2500) {
    return { ...current, phase: 'success', sawCommand, sawWorker };
  }
  return { ...current, sawCommand, sawWorker };
}

function replaceAction(template: string, action: string): string {
  return template.replace('{action}', action);
}

function userActionPhaseLabel(phase: UserActionModalPhase, t: (key: TranslationKey) => string): string {
  if (phase === 'sending') return t('actionModal.statusSending');
  if (phase === 'queued') return t('actionModal.statusQueued');
  if (phase === 'delivered') return t('actionModal.statusDelivered');
  if (phase === 'running') return t('actionModal.statusRunning');
  if (phase === 'success') return t('actionModal.statusSuccess');
  if (phase === 'timeout') return t('actionModal.statusTimeout');
  return t('actionModal.statusError');
}

function userActionModalPresentation(
  modal: UserActionModalState,
  status: BotStatus | null,
  commands: BotCommand[],
  now: number,
  t: (key: TranslationKey) => string
): {
  active: boolean;
  terminal: boolean;
  tone: OperationModalTone;
  eyebrow: string;
  title: string;
  body: string;
  detail: string | null;
  error: string | undefined;
  code: string | null;
  statusItems: { label: string; value: string }[];
} {
  const action = actionLabel(modal.action, t);
  const elapsedSeconds = Math.max(0, Math.floor((now - modal.startedAt) / 1000));
  const command = relevantUserActionCommand(modal, commands);
  const isStop = modal.action === 'stop';
  const terminal = isUserActionTerminal(modal.phase);
  const tone: OperationModalTone =
    modal.phase === 'success'
      ? 'success'
      : modal.phase === 'error' || modal.phase === 'timeout'
        ? 'error'
        : 'working';
  const phaseLabel = userActionPhaseLabel(modal.phase, t);

  let title = replaceAction(t('actionModal.workingTitle'), action);
  let body = replaceAction(t('actionModal.workingBody'), action);
  if (modal.phase === 'sending') {
    title = replaceAction(t('actionModal.sendingTitle'), action);
    body = replaceAction(t('actionModal.sendingBody'), action);
  } else if (modal.phase === 'queued') {
    title = replaceAction(t('actionModal.queuedTitle'), action);
    body = replaceAction(t('actionModal.queuedBody'), action);
  } else if (modal.phase === 'delivered') {
    title = replaceAction(t('actionModal.deliveredTitle'), action);
    body = replaceAction(t('actionModal.deliveredBody'), action);
  } else if (modal.phase === 'running') {
    title = isStop ? t('actionModal.stopRunningTitle') : replaceAction(t('actionModal.workingTitle'), action);
    body = isStop ? t('actionModal.stopRunningBody') : replaceAction(t('actionModal.workingBody'), action);
  } else if (modal.phase === 'success') {
    title = isStop ? t('actionModal.stopSuccessTitle') : replaceAction(t('actionModal.successTitle'), action);
    body = isStop ? t('actionModal.stopSuccessBody') : replaceAction(t('actionModal.successBody'), action);
    if (isStop && status?.screenBlocker?.kind) {
      body = t('actionModal.stopBlockedSuccessBody');
    }
  } else if (modal.phase === 'timeout') {
    title = replaceAction(t('actionModal.timeoutTitle'), action);
    body = replaceAction(t('actionModal.timeoutBody'), action);
  } else if (modal.phase === 'error') {
    title = replaceAction(t('actionModal.errorTitle'), action);
    body = modal.error === 'blocked'
      ? t('actionModal.blockedBody')
      : replaceAction(t('actionModal.errorBody'), action);
  }

  const detail =
    modal.error && modal.error !== 'blocked'
      ? modal.error
      : cleanLogLine(status?.latestLogLine, true) || command?.error || null;
  const detailText = modal.phase === 'error' ? null : detail;
  const errorText = modal.phase === 'error' && detail ? detail : undefined;

  return {
    active: !terminal,
    terminal,
    tone,
    eyebrow: t('actionModal.eyebrow'),
    title,
    body,
    detail: detailText,
    error: errorText,
    code: modal.commandId || command?.commandId || null,
    statusItems: [
      { label: t('operation.status'), value: phaseLabel },
      { label: t('operation.time'), value: `${elapsedSeconds}s` }
    ]
  };
}

function screenBlockerDetail(status: BotStatus, fallback: string, t: (key: TranslationKey) => string): string {
  const kind = String(status.screenBlocker?.kind || '').toLowerCase();
  if (kind === 'mobile_login') return t('dashboard.mobileLoginBlocker');
  return fallback || t('activity.blockedReason');
}

function dashboardActivity({
  status,
  commands,
  optimisticAction,
  commandPending,
  now,
  fallbackLine,
  hideSecretBotLogs,
  unavailableTitle,
  unavailableMessage,
  t
}: {
  status: BotStatus | null;
  commands: BotCommand[];
  optimisticAction: { action: ActionKey; startedAt: number } | null;
  commandPending: boolean;
  now: number;
  fallbackLine: string;
  hideSecretBotLogs: boolean;
  unavailableTitle: string;
  unavailableMessage: string;
  t: (key: TranslationKey) => string;
}): DashboardActivity {
  const detail = cleanLogLine(status?.latestLogLine, hideSecretBotLogs) || fallbackLine;
  const command = pendingCommand(commands);
  if (status?.screenBlocker) {
    return {
      kind: 'blocked',
      title: t('activity.blocked'),
      detail: screenBlockerDetail(status, detail, t),
      reason: t('activity.blockedReason'),
      showOverlay: true
    };
  }
  if (status?.reconnect?.running) {
    return {
      kind: 'running',
      title: t('activity.reconnecting'),
      detail: detail || t('dashboard.reconnectRequested'),
      showOverlay: true
    };
  }
  if (commandPending && optimisticAction) {
    return {
      kind: 'sending',
      title: t('activity.sending'),
      detail: `${actionLabel(optimisticAction.action, t)} · ${t('activity.contactingServer')}`,
      action: optimisticAction.action,
      showOverlay: true
    };
  }
  if (command) {
    return {
      kind: 'queued',
      title: command.status === 'delivered' ? t('activity.commandDelivered') : t('activity.commandQueued'),
      detail: `${actionLabel(command.action, t)} · ${t('activity.waitingWorker')}`,
      action: command.action,
      reason: t('activity.buttonsDelayed'),
      showOverlay: true
    };
  }
  if (status?.running) {
    return {
      kind: 'running',
      title: stateActivityTitle(status, t),
      detail: detail || t('activity.workerRunning'),
      showOverlay: true
    };
  }
  if (optimisticAction && now - optimisticAction.startedAt <= 45000) {
    return {
      kind: 'queued',
      title: t('activity.waitingConfirmation'),
      detail: `${actionLabel(optimisticAction.action, t)} · ${t('activity.refreshingStatus')}`,
      action: optimisticAction.action,
      reason: t('activity.buttonsDelayed'),
      showOverlay: true
    };
  }
  if (typeof status?.nextCheckSeconds === 'number' && status.nextCheckSeconds > 0) {
    return {
      kind: 'waiting',
      title: t('activity.waitingNextCheck'),
      detail,
      showOverlay: false
    };
  }
  if (status?.localAvailable === false || status?.bridgeStatus === 'offline') {
    return {
      kind: 'offline',
      title: unavailableTitle,
      detail: detail || unavailableMessage,
      reason: unavailableMessage,
      showOverlay: true
    };
  }
  return {
    kind: 'idle',
    title: t('activity.ready'),
    detail,
    showOverlay: false
  };
}

function BotStatusInfoBox({ activity }: { activity: DashboardActivity }) {
  return (
    <div className={`bot-status-info-box activity-${activity.kind}`} role="status" aria-live="polite">
      <span className="activity-spinner" />
      <div>
        <strong>{activity.title}</strong>
        <span>{activity.detail}</span>
      </div>
    </div>
  );
}

function ActionTimingStrip({
  timing
}: {
  timing: { runtimeSeconds: number; nextSeconds: number | null };
}) {
  return (
    <div className="action-timing-strip" aria-live="polite">
      <span>
        Runtime <b>{formatDuration(timing.runtimeSeconds)}</b>
      </span>
      <span>
        Siguiente <b>{timing.nextSeconds == null ? '-' : formatDuration(timing.nextSeconds)}</b>
      </span>
    </div>
  );
}

function OnboardingTutorialModal({
  doNotShow,
  onToggleDoNotShow,
  onClose,
  onConfirm
}: {
  doNotShow: boolean;
  onToggleDoNotShow: (value: boolean) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  const steps = [
    { title: t('tutorial.step1.title'), body: t('tutorial.step1.body') },
    { title: t('tutorial.step2.title'), body: t('tutorial.step2.body') },
    { title: t('tutorial.step3.title'), body: t('tutorial.step3.body') }
  ];

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="tutorial-modal" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
        <div className="tutorial-modal-header">
          <h2 id="tutorial-title">{t('tutorial.title')}</h2>
          <button type="button" className="icon-button" aria-label={t('tutorial.later')} onClick={onClose}>
            x
          </button>
        </div>
        <p>{t('tutorial.intro')}</p>
        <div className="tutorial-step-list">
          {steps.map((step) => (
            <section key={step.title} className="tutorial-step">
              <strong>{step.title}</strong>
              <span>{step.body}</span>
            </section>
          ))}
        </div>
        <label className="check tutorial-check">
          <input type="checkbox" checked={doNotShow} onChange={(event) => onToggleDoNotShow(event.target.checked)} />
          {t('tutorial.doNotShow')}
        </label>
        <div className="tutorial-actions">
          <button type="button" onClick={onClose}>
            {t('tutorial.later')}
          </button>
          <button type="button" className="primary" onClick={onConfirm}>
            {t('tutorial.start')}
          </button>
        </div>
      </div>
    </div>
  );
}

function IntelligenceQuotaModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  return (
    <div className="modal-backdrop quota-modal-backdrop" role="presentation">
      <div className="quota-modal" role="dialog" aria-modal="true" aria-labelledby="quota-modal-title">
        <button type="button" className="quota-modal-close" aria-label={t('intelligence.quotaClose')} onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="quota-modal-icon" aria-hidden="true">
          <SearchX size={28} />
        </div>
        <div className="quota-modal-copy">
          <span className="quota-modal-kicker">{t('intelligence.quotaKicker')}</span>
          <h2 id="quota-modal-title">{t('intelligence.quotaTitle')}</h2>
          <p>{t('intelligence.quotaBody')}</p>
        </div>
        <div className="quota-modal-limits">
          <div>
            <span>{t('intelligence.quotaTrialLabel')}</span>
            <strong>{t('intelligence.quotaTrialValue')}</strong>
          </div>
          <div>
            <span>{t('intelligence.quotaProLabel')}</span>
            <strong>{t('intelligence.quotaProValue')}</strong>
          </div>
          <div>
            <span>{t('intelligence.quotaProPlusLabel')}</span>
            <strong>{t('intelligence.quotaProPlusValue')}</strong>
          </div>
        </div>
        <div className="quota-modal-actions">
          <button type="button" onClick={onClose}>
            {t('intelligence.quotaClose')}
          </button>
          <a className="primary quota-upgrade-link" href="/#productos">
            {t('intelligence.quotaUpgrade')}
            <ArrowUpRight size={16} aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  );
}

function IntelligencePanel({
  requests,
  quota,
  loading,
  error,
  busy,
  deletingId,
  canDelete,
  defaultRegionId,
  onSubmit,
  onDelete
}: {
  requests: IntelligenceRequest[];
  quota: { limit: number; used: number; remaining: number };
  loading: boolean;
  error: unknown;
  busy: boolean;
  deletingId?: string;
  canDelete: boolean;
  defaultRegionId: string;
  onSubmit: (request: { playerName: string; regionId?: string }) => void;
  onDelete: (requestId: string) => void;
}) {
  const { t } = useI18n();
  const [playerName, setPlayerName] = useState('');
  const [regionId, setRegionId] = useState(defaultRegionId);
  const [panelOpen, setPanelOpen] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(() => new Set());
  const trimmedPlayerName = playerName.trim();
  const trimmedRegionId = regionId.trim();
  const canSubmit = trimmedPlayerName.length >= MIN_INTELLIGENCE_QUERY_LENGTH && !busy;
  const showMinHint = trimmedPlayerName.length > 0 && trimmedPlayerName.length < MIN_INTELLIGENCE_QUERY_LENGTH;

  useEffect(() => {
    setRegionId(defaultRegionId);
  }, [defaultRegionId]);

  return (
    <section className="panel intelligence-panel">
      <button
        type="button"
        className="dashboard-accordion-trigger intelligence-panel-trigger"
        aria-expanded={panelOpen}
        onClick={() => setPanelOpen((open) => !open)}
      >
        <span className="dashboard-accordion-icon intelligence-panel-icon" aria-hidden="true">
          <Radar size={19} />
        </span>
        <span className="dashboard-accordion-title">{t('intelligence.title')}</span>
        {quota.limit > 0 && (
          <span className="dashboard-accordion-meta">
            {t('intelligence.quotaRemainingValue')
              .replace('{remaining}', String(quota.remaining))
              .replace('{limit}', String(quota.limit))}
          </span>
        )}
        <ChevronDown className="dashboard-accordion-chevron" size={18} aria-hidden="true" />
      </button>
      {panelOpen && (
        <>
      <form
        className="intelligence-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!canSubmit) return;
          onSubmit({ playerName: trimmedPlayerName, regionId: trimmedRegionId || undefined });
          setPlayerName('');
        }}
      >
        <label className="intelligence-region-field">
          {t('intelligence.region')}
          <input
            type="text"
            value={regionId}
            placeholder={t('intelligence.regionPlaceholder')}
            disabled={busy}
            onChange={(event) => setRegionId(event.target.value)}
          />
        </label>
        <label>
          {t('intelligence.searchPlayer')}
          <input
            type="text"
            value={playerName}
            minLength={MIN_INTELLIGENCE_QUERY_LENGTH}
            placeholder={t('intelligence.searchPlaceholder')}
            disabled={busy}
            aria-describedby={showMinHint ? 'intelligence-min-chars' : undefined}
            onChange={(event) => setPlayerName(event.target.value)}
          />
          {showMinHint && (
            <span id="intelligence-min-chars" className="field-hint danger-hint">
              {t('intelligence.minChars')}
            </span>
          )}
        </label>
        <button type="submit" className="primary" disabled={!canSubmit}>
          {busy ? t('intelligence.requesting') : t('intelligence.request')}
        </button>
      </form>
      {quota.limit > 0 && (
        <div className={`intelligence-quota-strip ${quota.remaining <= 0 ? 'depleted' : quota.remaining <= 2 ? 'low' : ''}`}>
          <span>{t('intelligence.quotaRemaining')}</span>
          <strong>
            {t('intelligence.quotaRemainingValue')
              .replace('{remaining}', String(quota.remaining))
              .replace('{limit}', String(quota.limit))}
          </strong>
        </div>
      )}
      {Boolean(error) && <ErrorState error={error} />}
      {!error && loading && <EmptyState>{t('intelligence.loading')}</EmptyState>}
      {!error && !loading && !requests.length && <EmptyState>{t('intelligence.empty')}</EmptyState>}
      {!error && !loading && Boolean(requests.length) && (
        <div className="intelligence-request-list">
          {requests.map((request) => {
            const expanded = expandedRequests.has(request.id);
            return (
              <article className="intelligence-request-card" key={request.id}>
                <div className="intelligence-request-row">
                  <button
                    type="button"
                    className="intelligence-request-header"
                    aria-expanded={expanded}
                    onClick={() => {
                      setExpandedRequests((current) => {
                        const next = new Set(current);
                        if (next.has(request.id)) next.delete(request.id);
                        else next.add(request.id);
                        return next;
                      });
                    }}
                  >
                    <div className="intelligence-request-title">
                      <span>{t('intelligence.search')}</span>
                      <strong>{request.playerName || '-'}</strong>
                    </div>
                    <div className="intelligence-request-meta">
                      <span className={`intelligence-status intelligence-status-${request.status}`}>
                        {intelligenceStatusLabel(request, t)}
                      </span>
                      <span>{formatTimestamp(request.createdAt)}</span>
                      {(request.status === 'completed' || request.status === 'ready') && Boolean(request.results.length) && (
                        <span>{t('intelligence.resultsFound').replace('{count}', String(request.results.length))}</span>
                      )}
                    </div>
                    <ChevronDown className="intelligence-request-chevron" size={17} aria-hidden="true" />
                  </button>
                  {canDelete && (
                    <button
                      type="button"
                      className="icon-button intelligence-delete-button"
                      aria-label={`${t('intelligence.delete')} ${request.playerName || t('intelligence.search')}`}
                      title={t('intelligence.delete')}
                      disabled={deletingId === request.id}
                      onClick={() => {
                        const name = request.playerName || t('intelligence.search');
                        if (!window.confirm(t('intelligence.deleteConfirm').replace('{name}', name))) return;
                        onDelete(request.id);
                      }}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {expanded && (
                  <IntelligenceResultList
                    request={request}
                    expanded={expanded}
                    onToggle={() => {
                      setExpandedRequests((current) => {
                        const next = new Set(current);
                        if (next.has(request.id)) next.delete(request.id);
                        else next.add(request.id);
                        return next;
                      });
                    }}
                  />
                )}
              </article>
            );
          })}
        </div>
      )}
        </>
      )}
    </section>
  );
}

function IntelligenceResultList({
  request,
  expanded,
  onToggle
}: {
  request: IntelligenceRequest;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  const [sortBy, setSortBy] = useState<IntelligenceResultSort>('power');
  const pendingText = intelligenceResultLabel(request, t);
  if (request.status !== 'completed' && request.status !== 'ready') {
    return <span className="intelligence-result-muted">{pendingText}</span>;
  }

  if (!request.results.length) {
    return <span className="intelligence-result-muted">{t('intelligence.noResult')}</span>;
  }

  const sortedResults = sortIntelligenceResults(request.results, sortBy);
  const visibleResults = expanded
    ? sortedResults
    : sortedResults.slice(0, DEFAULT_VISIBLE_INTELLIGENCE_RESULTS);
  const hiddenCount = sortedResults.length - visibleResults.length;

  return (
    <div className="intelligence-results">
      <div className="intelligence-result-toolbar" aria-label={t('intelligence.sortResults')}>
        <span>{t('intelligence.sortResults')}</span>
        <div className="segmented-control">
          <button
            type="button"
            className={sortBy === 'power' ? 'active' : ''}
            onClick={() => setSortBy('power')}
          >
            {t('intelligence.sortPower')}
          </button>
          <button
            type="button"
            className={sortBy === 'name' ? 'active' : ''}
            onClick={() => setSortBy('name')}
          >
            {t('intelligence.sortName')}
          </button>
        </div>
      </div>
      <div className="intelligence-result-list">
        {visibleResults.map((result, index) => (
          <div
            className="intelligence-result-item"
            key={`${result.playerName || 'target'}-${result.castleX ?? 'x'}-${result.castleY ?? 'y'}-${index}`}
          >
            <div className="intelligence-result-main">
              <strong>{result.playerName || request.playerName}</strong>
              <span>{formatAlliance(result) || '-'}</span>
            </div>
            <div className="intelligence-result-meta">
              {targetMetaItems(result, t).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {(hiddenCount > 0 || expanded) && (
        <button type="button" className="link-button intelligence-more" onClick={onToggle}>
          {expanded
            ? t('intelligence.showLess')
            : t('intelligence.showMore').replace('{count}', String(hiddenCount))}
        </button>
      )}
    </div>
  );
}

function sortIntelligenceResults(
  results: IntelligenceRequest['results'],
  sortBy: IntelligenceResultSort
): IntelligenceRequest['results'] {
  const items = [...results];
  if (sortBy === 'name') {
    return items.sort((a, b) => String(a.playerName || '').localeCompare(String(b.playerName || ''), undefined, { sensitivity: 'base' }));
  }
  return items.sort((a, b) => {
    const powerDelta = (b.power ?? -1) - (a.power ?? -1);
    if (powerDelta !== 0) return powerDelta;
    return String(a.playerName || '').localeCompare(String(b.playerName || ''), undefined, { sensitivity: 'base' });
  });
}

function intelligenceStatusLabel(request: IntelligenceRequest, t: (key: TranslationKey) => string): string {
  if (request.status === 'pending') return t('intelligence.statusPending');
  if (request.status === 'processing' || request.status === 'searching_cache') return t('intelligence.statusProcessing');
  if (request.status === 'completed' || request.status === 'ready') return t('intelligence.statusCompleted');
  if (request.status === 'not_found') return t('intelligence.statusNotFound');
  return t('intelligence.statusFailed');
}

function intelligenceResultLabel(request: IntelligenceRequest, t: (key: TranslationKey) => string): string {
  if (request.status === 'pending') return t('intelligence.statusPending');
  if (request.status === 'processing' || request.status === 'searching_cache') return t('intelligence.statusProcessing');
  if (request.status === 'failed') return request.error || t('intelligence.statusFailed');
  if (request.status === 'not_found') return request.error || t('intelligence.notFound');
  if (request.status !== 'completed' && request.status !== 'ready') return request.error || t('intelligence.statusFailed');
  if (!request.results.length) return t('intelligence.noResult');
  return request.results.map((result) => {
    const alliance = formatAlliance(result);
    const coordinates = formatCoordinates(result, t);
    return [
      result.playerName || request.playerName,
      alliance,
      formatPower(result.power, t),
      coordinates
    ]
      .filter(Boolean)
      .join(' / ');
  }).join('\n');
}

function formatAlliance(result: IntelligenceRequest['results'][number]): string {
  return [result.allianceTag ? `[${result.allianceTag}]` : '', result.allianceName || ''].filter(Boolean).join(' ');
}

function formatCoordinates(result: IntelligenceRequest['results'][number], t: (key: TranslationKey) => string): string {
  return result.castleX != null && result.castleY != null
    ? `${t('intelligence.coordinates')} ${result.castleX}, ${result.castleY}`
    : '';
}

function formatPower(power: number | null | undefined, t: (key: TranslationKey) => string): string {
  return power != null ? `${t('intelligence.power')} ${power.toLocaleString()}` : '';
}

function targetMetaItems(result: IntelligenceRequest['results'][number], t: (key: TranslationKey) => string): string[] {
  return [
    formatPower(result.power, t),
    formatCoordinates(result, t),
    result.lastSeenAt ? `${t('intelligence.lastSeen')} ${formatTimestamp(result.lastSeenAt)}` : ''
  ].filter(Boolean);
}

// After this long without confirming, keep working but tell the user it's
// taking longer and let them close (the reconnect keeps running in background).
const RECONNECT_MODAL_SLOW_MS = 35000;
// Absolute give-up: only if the worker never settles (e.g. truly hung).
const RECONNECT_MODAL_TIMEOUT_MS = 300000;

type ReconnectModalKind = 'working' | 'success' | 'error';

interface ReconnectModalState {
  startedAt: number;
  sawWorking: boolean;
  kind: ReconnectModalKind;
  reason?: string | null;
  slow?: boolean;
}

const RECONNECT_WORKING_PHASES: ReadonlySet<string> = new Set([
  'requested',
  'tapping',
  'verifying',
  'scheduled'
]);

// Advance the reconnect modal using the live worker status. We only settle once
// we have actually seen this attempt running ("sawWorking") and then watched it
// stop, so a terminal phase left over from a previous attempt can't be misread.
//
// "Working" is driven primarily by status.running (the worker executing the
// post-reconnect startup check) and the account still being blocked, so this
// stays correct even if the backend doesn't report detailed reconnect phases
// yet. Success is the account no longer being blocked once the worker is idle;
// the backend's "recovered"/"failed" phases short-circuit it when available.
function advanceReconnectModal(
  current: ReconnectModalState | null,
  status: BotStatus | null
): ReconnectModalState | null {
  if (!current || current.kind !== 'working') return current;
  const reconnect = status?.reconnect;
  const phase = reconnect?.phase ?? null;
  const blocked = Boolean(status?.screenBlocker);
  const working =
    Boolean(reconnect?.running) ||
    Boolean(status?.running) ||
    (phase != null && RECONNECT_WORKING_PHASES.has(phase));
  const sawWorking = current.sawWorking || working;

  if (sawWorking && phase === 'recovered') {
    return { ...current, kind: 'success', sawWorking };
  }
  if (sawWorking && phase === 'failed') {
    return { ...current, kind: 'error', reason: reconnect?.result ?? 'generic', sawWorking };
  }
  // Settled (attempt ran, worker idle) and no longer blocked -> success.
  if (sawWorking && !working && !blocked) {
    return { ...current, kind: 'success', sawWorking };
  }
  // If it settled but still looks blocked, do NOT declare failure from this
  // heuristic alone: with an older worker (no phase) or a lagging bridge the
  // screen_blocker can be stale, and the account may be getting re-stolen right
  // after a real recovery. Stay "working" and let an explicit phase==="failed"
  // or the soft/absolute timeout decide, instead of flashing a false error.
  if (sawWorking !== current.sawWorking) {
    return { ...current, sawWorking };
  }
  return current;
}

function reconnectErrorReason(error: unknown): string {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
  if (message.includes('already running') || message.includes('409') || message.includes('busy')) {
    return 'busy';
  }
  return 'generic';
}

function reconnectModalCopy(
  kind: ReconnectModalKind,
  reason: string | null | undefined,
  t: (key: TranslationKey) => string
): { title: string; body: string } {
  if (kind === 'success') {
    return { title: t('reconnect.successTitle'), body: t('reconnect.successBody') };
  }
  if (kind === 'error') {
    if (reason === 'busy') return { title: t('reconnect.busyTitle'), body: t('reconnect.busyBody') };
    if (reason === 'timeout') return { title: t('reconnect.timeoutTitle'), body: t('reconnect.timeoutBody') };
    return { title: t('reconnect.errorTitle'), body: t('reconnect.errorBody') };
  }
  return { title: t('reconnect.workingTitle'), body: t('reconnect.workingBody') };
}

function ReconnectWaitModal({
  kind,
  reason,
  slow,
  canRetry,
  onRetry,
  onClose
}: {
  kind: ReconnectModalKind;
  reason?: string | null;
  slow?: boolean;
  canRetry: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const active = kind === 'working';
  const { title, body } = reconnectModalCopy(kind, reason, t);
  return (
    <OperationModal
      active={active}
      tone={kind === 'success' ? 'success' : kind === 'error' ? 'error' : 'working'}
      className={`reconnect-wait-modal reconnect-wait-${kind}`}
      titleId="reconnect-wait-title"
      eyebrow={t('reconnect.eyebrow')}
      title={title}
      body={body}
      detail={active && slow ? t('reconnect.slowNote') : undefined}
      actions={
        active && slow
          ? [{ label: t('reconnect.close'), onClick: onClose }]
          : !active
            ? [
                ...(kind === 'error' && canRetry
                  ? [{ label: t('reconnect.retry'), onClick: onRetry, primary: true }]
                  : []),
                { label: t('reconnect.close'), onClick: onClose }
              ]
            : []
      }
    />
  );
}

function ReconnectPanel({
  blocked,
  reconnect,
  settings,
  running,
  canSave,
  canReconnect,
  onSettingsChange,
  onReconnectNow
}: {
  blocked: boolean;
  reconnect: { running?: boolean; scheduled?: boolean; remainingSeconds?: number | null } | null | undefined;
  settings: ReconnectSettings;
  running: boolean;
  canSave: boolean;
  canReconnect: boolean;
  onSettingsChange: (settings: ReconnectSettings) => void;
  onReconnectNow: () => void;
}) {
  const { t } = useI18n();
  const remaining = reconnect?.remainingSeconds;
  const statusText = blocked
    ? reconnect?.running
      ? t('dashboard.reconnecting')
      : reconnect?.scheduled && remaining != null
        ? `Auto-reconexion en ${formatDuration(remaining)}`
        : t('dashboard.accountOtherDevice')
    : '';

  return (
    <Panel title="Reconexión">
      <div className={`reconnect-state ${blocked ? 'blocked' : ''}`}>
        <strong>{statusText}</strong>
      </div>
      <label className="check reconnect-toggle">
        <input
          type="checkbox"
          checked={settings.autoEnabled}
          disabled={!canSave}
          onChange={(event) => onSettingsChange({ ...settings, autoEnabled: event.target.checked })}
        />
        Reconectar auto
      </label>
      <label>
        Tiempo entre intentos
        <div className="number-stepper compact-stepper">
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSettingsChange({ ...settings, delaySeconds: settings.delaySeconds - 30 })}
          >
            -
          </button>
          <input
            aria-label="Tiempo entre intentos de reconexion"
            type="number"
            min={30}
            max={3600}
            step={30}
            value={settings.delaySeconds}
            disabled={!canSave}
            onChange={(event) => onSettingsChange({ ...settings, delaySeconds: Number(event.target.value) })}
          />
          <button
            type="button"
            disabled={!canSave}
            onClick={() => onSettingsChange({ ...settings, delaySeconds: settings.delaySeconds + 30 })}
          >
            +
          </button>
        </div>
      </label>
      <button className="primary" disabled={!canReconnect || running} onClick={onReconnectNow}>
        Reconectar ahora
      </button>
    </Panel>
  );
}

function SlotCapacityPanel({
  state,
  busy,
  onRetry
}: {
  state: SlotClaimState;
  busy: boolean;
  onRetry: () => void;
}) {
  const { t } = useI18n();
  const title =
    busy
      ? t('linking.claimingSlotTitle')
      : state === 'no_capacity'
        ? t('linking.noCapacityTitle')
        : state === 'error'
          ? t('linking.claimSlotProblemTitle')
          : t('linking.claimingSlotTitle');
  const body =
    busy
      ? t('linking.claimingSlotBody')
      : state === 'no_capacity'
        ? t('linking.noCapacityBody')
        : state === 'error'
          ? t('linking.claimSlotProblemBody')
          : t('linking.claimingSlotBody');

  return (
    <Panel title={t('linking.title')}>
      <div className="linking-box">
        <strong>{title}</strong>
        <span>{body}</span>
      </div>
      <button className="primary" disabled={busy} onClick={onRetry}>
        {busy ? t('linking.claimingSlotButton') : t('linking.retryCapacity')}
      </button>
    </Panel>
  );
}

function LinkingPanel({
  session,
  message,
  text,
  textVisible,
  sessionBusy,
  inputBusy,
  captureBusy,
  inputReady,
  inputKind,
  now,
  onStart,
  onTextChange,
  onToggleTextVisible,
  onSendText,
  onCancel,
  assistanceSupported,
  assistanceRequested,
  assistanceBusy,
  operatorActive,
  chatMessages,
  chatBusy,
  onRequestAssistance,
  onSendChat
}: {
  session: LinkingSession | null;
  message: string;
  text: string;
  textVisible: boolean;
  sessionBusy: boolean;
  inputBusy: boolean;
  captureBusy: boolean;
  inputReady: boolean;
  inputKind: 'email' | 'password';
  now: number;
  onStart: () => void;
  onTextChange: (text: string) => void;
  onToggleTextVisible: () => void;
  onSendText: () => void;
  onCancel: () => void;
  assistanceSupported: boolean;
  assistanceRequested: boolean;
  assistanceBusy: boolean;
  operatorActive: boolean;
  chatMessages: LinkingChatMessage[];
  chatBusy: boolean;
  onRequestAssistance: () => void;
  onSendChat: (body: string) => void;
}) {
  const { t } = useI18n();
  const secondsLeft = session ? Math.max(0, Math.floor((Date.parse(session.expiresAt) - now) / 1000)) : 0;
  const inputLocked = sessionBusy || operatorActive;
  const showAssistanceButton = !assistanceRequested && !operatorActive && chatMessages.length === 0;
  return (
    <Panel title={t('linking.title')}>
      <div className="linking-box">
        <strong>{session ? t('linking.activeSession') : t('linking.pendingAccount')}</strong>
        <span>{message}</span>
        {session && <em>{t('linking.expiresIn').replace('{time}', formatDuration(secondsLeft))}</em>}
      </div>
      {!session ? (
        <button className="primary" disabled={sessionBusy} onClick={onStart}>
          {t('linking.start')}
        </button>
      ) : !inputReady ? (
        <div className="linking-key-row">
          <button type="button" disabled={sessionBusy} onClick={onCancel}>
            {t('linking.cancel')}
          </button>
        </div>
      ) : (
        <>
          <label>
            {t('linking.textToSend')}
            <div className="linking-input-row">
              <input
                type={inputKind === 'password' && !textVisible ? 'password' : 'text'}
                value={text}
                disabled={inputLocked}
                placeholder={inputKind === 'password' ? t('linking.placeholderPassword') : t('linking.placeholder')}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                onChange={(event) => onTextChange(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onSendText();
                }}
              />
              <button
                type="button"
                className="icon-button"
                disabled={inputLocked || !text}
                onClick={onToggleTextVisible}
                title={textVisible ? t('linking.hideText') : t('linking.showText')}
                aria-label={textVisible ? t('linking.hideText') : t('linking.showText')}
              >
                {textVisible ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
              <button type="button" disabled={inputLocked || !text.trim()} onClick={onSendText} title={t('linking.send')}>
                <Send size={16} aria-hidden="true" />
                <span>{t('linking.send')}</span>
              </button>
            </div>
          </label>
          {(inputBusy || captureBusy) && <div className="linking-live-hint">{t('linking.inputQueued')}</div>}
        </>
      )}
      {session && assistanceSupported && (
        <div className="linking-assistance">
          {operatorActive && (
            <div className="notice linking-operator-banner">
              <strong>{t('linking.operatorActiveTitle')}</strong>
              <span>{t('linking.operatorActiveBody')}</span>
            </div>
          )}
          {showAssistanceButton ? (
            <button
              type="button"
              className="linking-assistance-button"
              disabled={assistanceBusy}
              onClick={onRequestAssistance}
            >
              <LifeBuoy size={16} aria-hidden="true" />
              <span>{t('linking.assistance')}</span>
            </button>
          ) : (
            <LinkingChatBox
              messages={chatMessages}
              busy={chatBusy}
              requested={assistanceRequested}
              onSend={onSendChat}
            />
          )}
        </div>
      )}
    </Panel>
  );
}

function LinkingChatBox({
  messages,
  busy,
  requested,
  onSend
}: {
  messages: LinkingChatMessage[];
  busy: boolean;
  requested: boolean;
  onSend: (body: string) => void;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const submit = () => {
    const body = draft.trim();
    if (!body) return;
    onSend(body);
    setDraft('');
  };
  return (
    <div className="linking-chat">
      <div className="linking-chat-header">
        <MessageCircle size={16} aria-hidden="true" />
        <strong>{t('linking.chatTitle')}</strong>
        {requested && <span className="linking-chat-pending">{t('linking.assistancePending')}</span>}
      </div>
      <div className="linking-chat-thread">
        {messages.length === 0 ? (
          <div className="linking-chat-empty">{t('linking.chatEmpty')}</div>
        ) : (
          messages.map((entry) => (
            <div
              key={entry.id}
              className={`linking-chat-message ${entry.senderRole === 'operator' ? 'from-operator' : 'from-user'}`}
            >
              <span className="linking-chat-sender">
                {entry.senderRole === 'operator' ? t('linking.senderOperator') : t('linking.senderYou')}
              </span>
              <span className="linking-chat-body">{entry.body}</span>
            </div>
          ))
        )}
      </div>
      <div className="linking-input-row">
        <input
          type="text"
          value={draft}
          placeholder={t('linking.chatPlaceholder')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <button type="button" disabled={busy || !draft.trim()} onClick={submit} title={t('linking.chatSend')}>
          <Send size={16} aria-hidden="true" />
          <span>{t('linking.chatSend')}</span>
        </button>
      </div>
    </div>
  );
}

function LinkingInfoModal({ title, body, onClose }: { title: string; body: string; onClose: () => void }) {
  return (
    <div className="modal-backdrop quota-modal-backdrop" role="presentation">
      <div className="quota-modal linking-info-modal" role="dialog" aria-modal="true" aria-labelledby="linking-info-title">
        <button type="button" className="quota-modal-close" aria-label="Close" onClick={onClose}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="quota-modal-icon" aria-hidden="true">
          <AlertTriangle size={26} />
        </div>
        <div className="quota-modal-copy">
          <span className="quota-modal-kicker">Linking</span>
          <h2 id="linking-info-title">{title}</h2>
          <p>{body}</p>
        </div>
        <div className="quota-modal-actions">
          <button type="button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

function LinkingWaitModal({
  title,
  body,
  status,
  detail,
  elapsedSeconds,
  active = true,
  actions = []
}: {
  title: string;
  body: string;
  status: string;
  detail?: string | null;
  elapsedSeconds: number;
  active?: boolean;
  actions?: { label: string; onClick: () => void; disabled?: boolean }[];
}) {
  const { t } = useI18n();
  return (
    <OperationModal
      active={active}
      tone={active ? 'working' : 'error'}
      className="linking-wait-modal"
      titleId="linking-wait-title"
      eyebrow={t('linking.title')}
      title={title}
      body={body}
      detail={detail}
      statusItems={[
        { label: t('operation.status'), value: status },
        { label: t('operation.time'), value: `${elapsedSeconds}s` }
      ]}
      actions={actions}
    />
  );
}

function ScreenshotFrame({
  src,
  state,
  linkingActive,
  waitMessage,
  waitHint,
  onTap
}: {
  src?: string | null;
  state: string;
  linkingActive?: boolean;
  waitMessage?: string | null;
  waitHint?: string | null;
  onTap?: (xRatio: number, yRatio: number) => void;
}) {
  const { t } = useI18n();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  useEffect(() => {
    setFailedSrc(null);
  }, [src]);

  if (src && failedSrc !== src) {
    return (
      <div className="screenshot-wrap">
        <img
          className={`screenshot-frame ${linkingActive ? 'linking-active' : ''}`}
          src={src}
          alt={t('dashboard.screenshot')}
          onError={() => setFailedSrc(src)}
          onClick={(event) => {
            if (!linkingActive || !onTap) return;
            const image = event.currentTarget;
            const rect = image.getBoundingClientRect();
            const naturalWidth = image.naturalWidth || rect.width;
            const naturalHeight = image.naturalHeight || rect.height;
            const naturalRatio = naturalWidth / naturalHeight;
            const boxRatio = rect.width / rect.height;
            let renderedWidth = rect.width;
            let renderedHeight = rect.height;
            let offsetX = 0;
            let offsetY = 0;

            if (boxRatio > naturalRatio) {
              renderedWidth = rect.height * naturalRatio;
              offsetX = (rect.width - renderedWidth) / 2;
            } else {
              renderedHeight = rect.width / naturalRatio;
              offsetY = (rect.height - renderedHeight) / 2;
            }

            const x = event.clientX - rect.left - offsetX;
            const y = event.clientY - rect.top - offsetY;
            if (x < 0 || y < 0 || x > renderedWidth || y > renderedHeight) return;
            onTap(x / renderedWidth, y / renderedHeight);
          }}
        />
        {waitMessage && <ScreenshotWaitOverlay message={waitMessage} hint={waitHint} />}
      </div>
    );
  }
  return (
    <div className="screenshot-wrap">
      <div className="mock-screenshot" aria-label="Captura simulada">
        <div className="mock-game-top">
          <span>WHITE BOT</span>
          <strong>{state}</strong>
        </div>
        <div className="mock-game-map">
          <div className="mock-base" />
          <div className="mock-path path-a" />
          <div className="mock-path path-b" />
        </div>
        <div className="mock-game-bottom">{t('dashboard.mockView')}</div>
      </div>
      {waitMessage && <ScreenshotWaitOverlay message={waitMessage} hint={waitHint} />}
    </div>
  );
}

function ScreenshotWaitOverlay({ message, hint }: { message: string; hint?: string | null }) {
  return (
    <div className="screenshot-wait-overlay" aria-live="polite">
      <div className="linking-spinner" aria-hidden="true" />
      <strong>{message}</strong>
      {hint && <span>{hint}</span>}
    </div>
  );
}

function QueuePanel({ title, queues }: { title: string; queues: { queue: number; action: string; remaining?: string | null }[] }) {
  const { t } = useI18n();
  return (
    <Panel title={title}>
      {!queues.length && <EmptyState>{t('dashboard.noRecentVerification')}</EmptyState>}
      {queues.map((queue) => (
        <div className="queue-row" key={`${title}-${queue.queue}`}>
          <strong>{queue.action}</strong>
          <span>{queue.remaining || '-'}</span>
        </div>
      ))}
    </Panel>
  );
}
